// YtreTab.jsx — YouTube Response Engine workspace.
//
// Randy Feb 2026 (pt-ytre-p1). Backend spine lives in
// /app/backend/routes/ytre.py; this file is the frontend counterpart.
//
// Flow:
//   1. Paste a YouTube URL → creates a working-copy session server-side,
//      pulls transcript in one shot (61 entries typical for a 3-4min video).
//   2. Embedded YouTube player with native controls intact (spacebar, J/L,
//      arrows, double-tap-to-seek all work — YouTube's iframe handles them).
//   3. Big "Capture point" button + "Minutes prior" picker below the video.
//      Tapping Capture pulls a transcript slice from
//      (current_time - minutes_prior*60) → current_time (or from the last
//      injection pointer when minutes_prior is unset) into the workspace.
//   4. Each transcript card in the workspace has:
//        - Raw / Revised toggle (once a Workbench op has been run)
//        - Workbench-op dropdown at the bottom (loops to /workbench/run)
//        - "Ask silo" button → nudge picker → silo response card appended
//        - Edit button → inline textarea
//   5. Silo response cards render the answer + which nudge was applied.
//   6. Notes are free-text cards anchored to any timestamp.
//   7. Saved projects strip at the bottom of the tab — swap the URL and
//      the "save first?" CTA fires.
//
// Deferred (iter160+): master reply modal, post-to-YouTube, send-to-silo /
// send-to-workbench / copy exports. This turn ships the workspace spine.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { PlayCircle, Sparkle, Lightning, CaretDown, CaretRight, CaretUp, PencilSimple, Trash, FloppyDisk, X, ChatCircleText, ArrowsClockwise, MagicWand, PaperPlaneRight, Copy as CopyIcon, ShareNetwork, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { ytApi } from "@/lib/ytApi";
import YTRedraftCapturePad from "@/components/YTRedraftCapturePad";
import GuidanceChip from "@/components/GuidanceChip";
import { runYtreAsyncJob } from "@/lib/asyncJob";
import YtreYouTubeConnectCard from "@/components/YtreYouTubeConnectCard";
import DocSaveDialog from "@/components/DocSaveDialog";
import YtreCustomNudgeModal from "@/components/YtreCustomNudgeModal";
import CustomCommandsModal from "@/components/CustomCommandsModal";
import YtreLoadGateModal from "@/components/YtreLoadGateModal";
import YtreChannelSwitchModal from "@/components/YtreChannelSwitchModal";
import { YtreChannelPicker } from "@/components/YtreChannelPicker";
import SessionLlmPickerModal from "@/components/SessionLlmPickerModal";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import UserCommentCard from "@/components/UserCommentCard";
import VoiceOriginalPrompt, { editRatio, ASK_THRESHOLD } from "@/components/VoiceOriginalPrompt";
import RecaptureButton from "@/components/RecaptureButton";
import LengthStagePicker, { prefetchAnswerStyles } from "@/components/LengthStagePicker";
import TonePickerPill from "@/components/TonePickerPill";
import YtreCanvasSearch from "@/components/YtreCanvasSearch";
import { computeCanvasMatches, renderWithMarks } from "@/lib/ytreSearchHighlight";
import { readSessionLlmOverride, labelForModel, overrideAsAskBody } from "@/lib/sessionLlm";
import { SlidersHorizontal } from "@phosphor-icons/react";
import { writeStaging } from "@/lib/stagingBuffer";
import { readCardSettings, writeCardSettings } from "@/lib/cardSettingsStore";
import { useCardDraft } from "@/lib/useCardDraft";
import { draftAgeLabel } from "@/lib/ytreDraftCache";
import { useThreeStageChevron } from "@/hooks/useAnchoredCollapse";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

// The 3-stage palindromic chevron hook now lives at
// /app/frontend/src/hooks/useAnchoredCollapse.js so every card-collapse
// surface on the platform can share the same anchor-scroll + smooth-
// transition behavior. Iter160t (Randy pt-chevron-platform-sweep,
// Feb 2026) — extracted and applied platform-wide.

// Iter160s / Iter160u (Randy pt-ytre-toast-fix) — pattern-match structured
// error payloads from the silo pipeline. Randy's audit: seeing the raw JSON
// blob dumped as a toast for OUT_OF_INTERACTIONS was embarrassing + technically
// WRONG (the owner-JWT loopback fix should stop that code from ever firing on
// YTRE now). This helper is a safety net for any structured detail objects
// the pipeline might surface in the future. Module-scope so BOTH YtreTab and
// MasterReplyModal can use it — the master-reply modal was previously falling
// back to raw JSON dumps because it didn't have access to the inner helper.
function friendlyPipelineError(e, fallback = "Silo pipeline call failed.") {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length) {
    // Pydantic 422s: array of {loc, msg, type, ...}. Pull the first msg.
    const first = detail[0];
    if (first && typeof first === "object") {
      const loc = Array.isArray(first.loc) ? first.loc.join(".") : "";
      return loc ? `${first.msg} (field: ${loc})` : String(first.msg || fallback);
    }
  }
  if (detail && typeof detail === "object") {
    const code = detail.code;
    if (code === "OUT_OF_INTERACTIONS") {
      return "The silo pipeline treated this call as an anonymous visitor. Reload the tab — the owner-JWT loopback likely needs to remint.";
    }
    if (code === "LLM_GUARD") {
      const prov = detail.provider || "the model";
      return `You're out of ${prov} fuel on this silo. Top up in Master Dashboard → Manage plan.`;
    }
    if (detail.message) return String(detail.message);
  }
  return fallback;
}


// Iter160c (Randy) — nudges live in the platform's global_commands /
// custom_commands collections with scope="ytre" now (same infra as slots
// + refine_silo). Fetched via GET /api/commands?scope=ytre. Kept a tiny
// palette here so the badge on each silo-response card can hint at
// stance visually without leaking backend knowledge.
const NUDGE_PALETTE = ["#57534E", "#C16E5A", "#B98A47", "#4A8060", "#5A7DBF", "#8060A5"];
const colorForNudge = (nudgeCommandId) => {
  if (!nudgeCommandId) return NUDGE_PALETTE[0];
  let h = 0;
  for (const c of nudgeCommandId) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return NUDGE_PALETTE[h % NUDGE_PALETTE.length];
};

// Fixed length presets — same three-shape hierarchy the backend
// `/master-reply` endpoint AND the /ask-silo `length` hint accept.
// Staging-only in the UI: picking a length just remembers it; it doesn't
// fire the LLM until the user taps Generate or Re-draft.
// Iter160l (Randy pt-length-picker-unification finish, Feb 2026) — the
// last hardcoded length-preset list was retired here. YTRE now uses
// `<LengthStagePicker>` everywhere (TranscriptCard action row,
// SiloResponseCard re-draft, and the Master Reply modal), sourced from
// the shared `/api/s/{slug}/answer-styles` registry — same source Public
// Q&A + YT Replies read from. If a silo owner adds a custom preset, all
// three surfaces pick it up automatically.

// Minutes-prior slider bounds (Randy Feb 2026 iter160b — slider 1..15 min,
// with a separate "Since last capture" default when the slider is at the
// zero position).
// Feb 2026 (Randy pt-ytre-mprior-subminute · iter169-Phase1) — expanded
// to sub-minute presets. Discrete slider now steps through
// [15s, 30s, 1m, 2m, 3m, 5m, 8m, 10m, 15m]. Slider value is the ARRAY
// INDEX (0..8); the actual capture window is `MPRIOR_PRESETS[index]`
// minutes (fractional for sub-minute values). Sent to backend as
// `minutes_prior: float` (0.25 = 15s, 0.5 = 30s, 1 = 1m, ...).
const MPRIOR_PRESETS = [0.25, 0.5, 1, 2, 3, 5, 8, 10, 15]; // minutes
const MPRIOR_LABELS  = ["15s", "30s", "1m", "2m", "3m", "5m", "8m", "10m", "15m"];
const MPRIOR_MIN_IDX = 0;
const MPRIOR_MAX_IDX = MPRIOR_PRESETS.length - 1;

function fmtTime(sec) {
  if (sec == null || Number.isNaN(sec)) return "";
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${String(ss).padStart(2, "0")}`;
}

export default function YtreTab({ siloId, siloName, siloSlug, active, allSilos = [] }) {
  const [urlInput, setUrlInput] = useState("");
  // Iter171 Ask 1 (Randy pt-ytre-loading-hint, Feb 13 2026) — URL load
  // takes 10-15s. Cycle a stage-hint through the spinner so users know
  // it's actually working, not stuck. Stage-hint advances on a timer
  // since real backend milestones aren't cheap to stream back.
  const [loadStageHint, setLoadStageHint] = useState("");
  // Iter171 Ask 4 (Randy pt-ytre-url-memory) — last 5 URLs pasted on
  // this silo, fetched on tab mount + after every successful load.
  const [urlHistory, setUrlHistory] = useState([]);
  const [urlHistoryOpen, setUrlHistoryOpen] = useState(false);
  // Feb 24, 2026 (Randy — wrong-channel banner) — when YouTube returns
  // comments for the loaded video but ZERO match the currently-
  // connected channel, the /from-url and /refresh-from-youtube
  // responses ship a `channel_match` payload:
  //   { total_threads, matched_threads, connected_channel_id,
  //     connected_channel_title, integration_present }
  // We stash it here so the workspace can surface the warning
  // banner ("691 comments on this video, 0 from your connected
  // channel 'Cosmic Equivalence'"). Reset on new URL loads.
  const [channelMismatch, setChannelMismatch] = useState(null);
  // Feb 26, 2026 (Randy pt-mismatch-banner-dismiss) — session-scoped
  // dismiss for the wrong-channel banner. Simple state (no persistence
  // across page reloads or URL switches). Randy explicitly asked for
  // the simplest possible thing: "just add an X so it can be
  // dismissed." Banner reappears if the mismatch still holds after
  // reload/URL-switch, which is fine — that's the reminder loop.
  const [mismatchBannerDismissed, setMismatchBannerDismissed] = useState(false);
  // Sep 25, 2026 (Randy) — YouTube's daily allowance. A quota rejection used
  // to be swallowed into an empty comment list, so a used-up allowance looked
  // identical to a video with no comments. The load / refresh responses now
  // carry `channel_match.quota_exceeded`; this state is set ONLY from a live
  // load or refresh (never from the persisted session doc) so the notice can
  // never outlive the day it happened on.
  const [quotaNotice, setQuotaNotice] = useState(null);
  const [retryingTranscript, setRetryingTranscript] = useState(false);
  const [transcriptProbe, setTranscriptProbe] = useState(null);
  // Feb 24, 2026 (Randy — YTRE activity alerts) — unread-activity
  // banner state. Shape: [{ video_id, video_title, url, unread_activity: {...} }].
  // Fetched on tab mount from /ytre/silo/{id}/activity — the scan
  // itself is fired by SiloManage.jsx when the silo mounts, so by
  // the time YTRE mounts the state is fresh.
  const [activityAlerts, setActivityAlerts] = useState([]);
  // Iter171 Ask 5 (Randy pt-ytre-length-cascade) — silo-level YTRE
  // default Length preset id. Seeded from /silo/{siloId}/default-length
  // on tab mount. Transcript card Length changes update this AND POST
  // to the endpoint so future capture-points + URL-loads inherit.
  const [siloDefaultLength, setSiloDefaultLength] = useState("standard");
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [session, setSession] = useState(null);           // active working copy
  const [entries, setEntries] = useState([]);
  const [savedProjects, setSavedProjects] = useState([]);
  const [nudges, setNudges] = useState([]);              // scope="ytre" commands (global + user custom)
  const [commands, setCommands] = useState([]);           // Workbench ops (scope="slots")
  const [currentTime, setCurrentTime] = useState(0);      // playhead
  // Iter160b — minutes-prior is now a 1..15 slider with a separate
  // "Since last capture" toggle. When `useSinceLast` is true, the slider
  // is ignored and the backend computes the start of the capture window
  // LIVE from the current canvas (max time_end_sec across transcript
  // entries, fallback 0). Iter160x (Randy pt-ytre-capture-cursor-fix,
  // Feb 2026) — was previously reading a stale session-level cursor
  // that didn't respect card deletions.
  const [minutesPrior, setMinutesPrior] = useState(3);
  // Feb 2026 (iter169-Phase1) — slider is index-based over MPRIOR_PRESETS.
  // Preserve minutesPrior as the actual float minutes so backend contract
  // stays the same (backend accepts float now).
  const mpriorIdx = Math.max(0, MPRIOR_PRESETS.indexOf(minutesPrior));
  // Iter160e (Randy) — default OFF so the slider is interactive from the
  // start. Users can still tick it on for the "use whatever's since my
  // last capture" shortcut; the slider greys out when checked, which is
  // fine — it's the checkbox default that was confusing.
  const [useSinceLast, setUseSinceLast] = useState(false);
  const effectiveMinutesPrior = useSinceLast ? null : minutesPrior;
  // Iter160x (Randy pt-ytre-capture-cursor-fix, Feb 2026) — the "Last
  // capture end" hint next to the slider must mirror the LIVE canvas
  // (post-deletion accurate), not the stale session-level
  // `last_injection_at_sec` cursor. This is the same math the backend
  // does when the checkbox is on. When the user trashcans their most
  // recent transcript card, this number naturally rolls back to the
  // previous capture's end — closing the loop Randy audit spotted.
  const liveLastCaptureEnd = useMemo(() => {
    let mx = 0;
    for (const e of entries) {
      if (e?.kind !== "transcript") continue;
      const v = Number(e?.time_end_sec) || 0;
      if (v > mx) mx = v;
    }
    return mx;
  }, [entries]);
  const [capturing, setCapturing] = useState(false);
  const [busyEntryId, setBusyEntryId] = useState(null);   // for op / ask-silo spinners
  // Iter172 (Randy pt-ytre-refresh-and-minimize, Feb 13, 2026) — two
  // small buttons flanking the top of the workspace:
  //
  //   • REFRESH REPLY HISTORY (left) — cheap re-fetch of the current
  //     session's entries via /ytre/sessions/working. Zero LLM calls,
  //     zero YouTube API calls, ~1 DB read. Randy asked for this so
  //     new server-side changes (e.g. background auto-drafts, cross-
  //     tab activity) surface without a full URL reload.
  //   • MINIMIZE ALL (right) — drives EVERY card to stage-2 (header-
  //     only). Uses a monotonically increasing tick that each card
  //     watches via useEffect. React strict-mode-safe (imperative
  //     setStage on useThreeStageChevron).
  const [refreshingHistory, setRefreshingHistory] = useState(false);
  const [minimizeTick, setMinimizeTick] = useState(0);
  // Sep 10 2026 (Randy) — the button toggles. `minimizeStage` is the
  // chevron stage every card jumps to when the tick bumps: 2 =
  // header-only, 1 = the MIDDLE open state (preview + footer), which is
  // what Randy wants "Open all" to mean — not stage 0 / fully open.
  const [minimizeStage, setMinimizeStage] = useState(2);
  const [allMinimized, setAllMinimized] = useState(false);
  // Iter172 audit-7 (Randy pt-ytre-canvas-search, Feb 17, 2026) —
  // canvas-wide plain-substring search. Sits inline in the workspace
  // toolbar between Refresh and Minimize all. Owns:
  //   * `searchQuery` — the raw input text
  //   * `searchMatches` — flat list of {entryId, occIdx, start, end}
  //     computed from `entries` × `searchQuery`, in canvas order
  //   * `activeMatchIdx` — index into searchMatches
  //   * `revealTargetId` + `revealNonce` — signal to cards that they
  //     should expand to stage-1 IF they're currently at stage-2 (so
  //     search doesn't force-collapse anything the user has left open).
  //     Cards visited during a search tour end at stage-1; other cards
  //     stay where the user left them.
  //   * `searchWrapPulseTick` — bumps when next/prev wraps around, so
  //     the counter pulses briefly (helps mobile users see the cycle).
  const [searchQuery, setSearchQuery] = useState("");
  const [activeMatchIdx, setActiveMatchIdx] = useState(0);
  const [revealTargetId, setRevealTargetId] = useState(null);
  const [revealNonce, setRevealNonce] = useState(0);
  const [searchWrapPulseTick, setSearchWrapPulseTick] = useState(0);
  // Iter160y (Randy pt-ytre-load-flow-fix, Feb 2026) — the save-first
  // CTA now covers BOTH entry points that swap the working copy:
  //   { kind: "url", url }             → URL paste
  //   { kind: "project", projectId }   → tap on a saved-project tile
  // Randy audit: tapping a saved project used to open a native
  // `window.confirm` (ugly, no discard option). It now reuses this
  // styled modal so the three-button UX is identical to URL paste.
  const [saveFirstPrompt, setSaveFirstPrompt] = useState(null);
  const playerRef = useRef(null);
  const playerReadyRef = useRef(false);

  // Iter160s (Randy pt-ytre-family-mirror, Feb 2026) — family-level
  // staging state, keyed by TRANSCRIPT entry id. Every card in a
  // transcript's family (the transcript itself + its silo_comment /
  // user_comment children) reads and writes to the SAME entry in
  // these two maps. This is what makes the footer pills on the
  // children act as live mirrors of the transcript's pills — change
  // it anywhere, it changes everywhere in the family.
  //
  //   familyNudge:  { [transcriptId]: { id, label, is_custom } | null }
  //   familyLength: { [transcriptId]: presetId | null }
  //
  // A child comment finds its family root via entry.source_entry_id
  // (which points back to the transcript that spawned it).
  const [familyNudge, setFamilyNudge] = useState({});
  const [familyLength, setFamilyLength] = useState({});

  // Iter160u (Randy pt-length-slow-load-fix, Feb 2026) — warm the
  // answer-styles cache on YtreTab mount so the Length pill in every
  // card footer renders instantly the FIRST time the user clicks it.
  // Randy: "anytime you click on the length dropdown it literally takes
  // anywhere from 30 seconds to a minute to load." Fixed via a
  // module-scope prefetch that runs the /answer-styles fetch on mount,
  // populates the LengthStagePicker's shared cache, so every Length
  // popover on the tab shows items instantly instead of "Loading…".
  useEffect(() => {
    if (siloSlug) prefetchAnswerStyles(siloSlug);
  }, [siloSlug]);

  // Iter171 Ask 4 + Ask 5 — on tab/silo change, fetch URL history and
  // silo default Length in parallel so both are ready when the user
  // opens the dropdown / lands a new card.
  useEffect(() => {
    if (!siloId) return;
    (async () => {
      try {
        const [hist, defLen, activity] = await Promise.all([
          api.get(`/ytre/url-history?silo_id=${siloId}`).catch(() => ({ data: {} })),
          api.get(`/ytre/silo/${siloId}/default-length`).catch(() => ({ data: {} })),
          api.get(`/ytre/silo/${siloId}/activity`).catch(() => ({ data: {} })),
        ]);
        setUrlHistory(hist.data?.history || []);
        setSiloDefaultLength(defLen.data?.length || "standard");
        // Feb 24, 2026 (Randy — YTRE activity alerts) — hydrate the
        // top-of-workspace banner from the cached unread state. Scan
        // itself is fired by SiloManage.jsx on mount, so by the time
        // this hits Mongo the state has been freshly refreshed.
        setActivityAlerts(activity.data?.unread_urls || []);
      } catch { /* silent */ }
    })();
  }, [siloId]);

  // Iter171 Ask 5 — POST the silo default length whenever it changes
  // from a transcript card. Randy's spec: transcript-level Length
  // change = new default for future capture-points + URL-loads.
  const saveSiloDefaultLength = useCallback(async (newLength) => {
    if (!siloId || !newLength) return;
    setSiloDefaultLength(newLength);  // optimistic
    try {
      await api.patch(`/ytre/silo/${siloId}/default-length`, { length: newLength });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save default length.");
    }
  }, [siloId]);

  const stageFamilyNudge = (transcriptId, nudge) => {
    setFamilyNudge((prev) => ({ ...prev, [transcriptId]: nudge }));
  };
  const stageFamilyLength = (transcriptId, presetId) => {
    setFamilyLength((prev) => ({ ...prev, [transcriptId]: presetId }));
  };

  // Iter171 Ask 5 → iter172 (Randy pt-ytre-per-card-settings, Feb 13,
  // 2026) — REMOVED the seed-from-silo-default useEffect. Randy's
  // narrowed scope: the silo `ytre_default_length` now governs ONLY
  // the auto-draft-on-URL-load flow (applied server-side inside
  // `_build_child_context_prompt` via `length_override`). In-session
  // card Length is per-card local state, defaults to "standard" on
  // new session or URL reload (React state naturally resets). No
  // cascade. Randy tunes the auto-draft default via the new "Set
  // auto response Length" thin card at the bottom of the YTRE tab.

  // Boot: load working copy + saved projects + nudges + commands + pin-pref
  // in parallel. Feb 2026 (Randy pt-ytre-pin-persist) — pull the per-silo
  // `ytre_pin_video` ui_pref alongside the working copy so the pin toggle
  // reflects the user's last choice, not the hardcoded server default.
  const [pinPrefLoaded, setPinPrefLoaded] = useState(false);
  const pinPrefRef = useRef(null); // last-known user preference for pin_video (bool or null)
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    (async () => {
      try {
        const [wc, saved, nz, cmds, prefs] = await Promise.all([
          api.get("/ytre/sessions/working"),
          api.get("/ytre/sessions"),
          api.get("/commands?scope=ytre"),
          api.get("/commands?scope=slots"),
          api.get("/me/ui-prefs", { params: { silo_id: siloId } }).catch(() => ({ data: { prefs: {} } })),
        ]);
        if (cancelled) return;
        const pinPref = (prefs.data?.prefs || {}).ytre_pin_video;
        pinPrefRef.current = (typeof pinPref === "boolean") ? pinPref : null;
        setPinPrefLoaded(true);
        if (wc.data?.working_copy) {
          const wcSession = wc.data.working_copy;
          // Apply the user's persisted pin preference over the session's
          // stored value so returning to YTRE always reflects the user's
          // last choice, even if this session was created before the
          // preference existed.
          const patched = pinPrefRef.current !== null
            ? { ...wcSession, pin_video: pinPrefRef.current }
            : wcSession;
          setSession(patched);
          setEntries(wc.data.entries || []);
          // Feb 24, 2026 (Randy — wrong-channel banner) — hydrate the
          // last-known channel-match diagnostic from the persisted
          // session doc so the banner survives page refreshes.
          setChannelMismatch(wc.data?.channel_match || null);
          // Iter160y (Randy pt-ytre-url-persist, Feb 2026) — the URL
          // input mirrors the current session's video URL on mount so
          // it survives page refresh / tab close. Randy: "I would like
          // that URL to stay server-side in the text box instead of
          // deleting."
          if (wcSession.video_url) setUrlInput(wcSession.video_url);
          // Auto-link this session to the current silo if unset.
          if (!wcSession.silo_id) {
            await api.patch(`/ytre/sessions/${wcSession.id}`, { silo_id: siloId });
            setSession((s) => (s ? { ...s, silo_id: siloId } : s));
          }
          // If pin_video on the session diverges from the preference,
          // silently reconcile the session to match.
          if (pinPrefRef.current !== null && wcSession.pin_video !== pinPrefRef.current) {
            try { await api.patch(`/ytre/sessions/${wcSession.id}`, { pin_video: pinPrefRef.current }); } catch { /* */ }
          }
        }
        setSavedProjects(saved.data?.projects || []);
        setNudges(nz.data || []);
        setCommands(cmds.data || []);
      } catch (e) {
        toast.error("Couldn't load YTRE workspace.");
      }
    })();
    return () => { cancelled = true; };
  }, [active, siloId]);

  // Wire the YouTube iframe API postMessage bridge so we can (a) track the
  // playhead as it moves and (b) know when the player is ready. The
  // published protocol (undocumented but stable — used by every embed on
  // the web):
  //   1. On iframe load, we send  {event:"listening", id, channel:"widget"}.
  //      YouTube then knows to emit `infoDelivery` packets to `window`.
  //   2. YouTube emits `{event:"infoDelivery", info:{currentTime, ...}}`
  //      automatically at ~4 Hz while the player is running — no polling
  //      needed on our side.
  //   3. As a safety net (some browsers throttle the auto stream), we
  //      also send periodic `getCurrentTime` command probes.
  useEffect(() => {
    if (!session?.video_id) return;
    const iframe = playerRef.current;
    if (!iframe) return;

    const sendListening = () => {
      try {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ event: "listening", id: iframe.id, channel: "widget" }),
          "*"
        );
      } catch { /* */ }
    };
    const sendProbe = () => {
      try {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ event: "command", func: "getCurrentTime", args: [] }),
          "*"
        );
      } catch { /* */ }
    };

    const onMsg = (ev) => {
      // Only consume messages that originate from a youtube.com iframe.
      if (typeof ev.origin === "string" && !ev.origin.includes("youtube")) return;
      let data = ev.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch { return; }
      }
      if (!data || typeof data !== "object") return;

      if (data.event === "onReady" || data.event === "initialDelivery") {
        playerReadyRef.current = true;
        // Iter160d: debug breadcrumb — Randy can F12 → Console to
        // confirm the postMessage handshake is alive (verifies the
        // fix survived any browser cache).
        console.debug("[YTRE] player ready — postMessage bridge live");
        sendProbe();
        return;
      }
      if (data.event === "infoDelivery" && data.info) {
        if (typeof data.info.currentTime === "number") {
          setCurrentTime(Math.floor(data.info.currentTime));
        }
      }
    };

    window.addEventListener("message", onMsg);
    // Register listening immediately AND on iframe load — the iframe is
    // often already loaded by the time this effect runs (React StrictMode
    // remounts), so hitting `load` alone would miss the first video.
    sendListening();
    iframe.addEventListener("load", sendListening);

    // Safety net probe every 500ms. Cheap, and unblocks any browser where
    // the auto `infoDelivery` stream is throttled.
    const probeInterval = setInterval(sendProbe, 500);

    return () => {
      window.removeEventListener("message", onMsg);
      iframe.removeEventListener("load", sendListening);
      clearInterval(probeInterval);
      playerReadyRef.current = false;
    };
  }, [session?.video_id]);

  const loadUrl = useCallback(async (opts = {}) => {
    const url = (opts.url || urlInput).trim();
    if (!url) { toast.error("Paste a YouTube URL first."); return; }
    setLoadingUrl(true);
    // Iter171 Ask 1 — stage-hint spinner
    setLoadStageHint("Fetching video metadata…");
    const hintTimers = [
      setTimeout(() => setLoadStageHint("Loading transcript…"), 3500),
      setTimeout(() => setLoadStageHint("Fetching your comment thread…"), 8000),
      setTimeout(() => setLoadStageHint("Almost there…"), 13000),
    ];
    try {
      const body = { video_url: url, silo_id: siloId };
      // Iter160f (Randy Wave-2) — always try to preload the user's
      // existing comments on this video. Backend silently returns an
      // empty list if the user isn't OAuth-connected or has no
      // matching comments — so this is a zero-cost opt-in.
      body.preload_existing_comment = true;
      if (opts.save === true) {
        body.save_current_working_copy = true;
        body.save_title = opts.saveTitle || session?.video_title || "Untitled project";
      } else if (opts.save === false) {
        body.save_current_working_copy = false;
      }
      const r = await api.post("/ytre/sessions/from-url", body);
      const freshSess = r.data.session;
      // Feb 2026 (Randy pt-ytre-pin-persist) — new sessions default
      // pin_video=true server-side. Overlay the user's stored preference
      // (via ui_prefs.ytre_pin_video) so returning users don't have to
      // re-toggle on every URL load.
      let mergedSess = freshSess;
      if (pinPrefRef.current !== null && freshSess) {
        if (freshSess.pin_video !== pinPrefRef.current) {
          try { await api.patch(`/ytre/sessions/${freshSess.id}`, { pin_video: pinPrefRef.current }); } catch { /* silent */ }
        }
        mergedSess = { ...freshSess, pin_video: pinPrefRef.current };
      }
      setSession(mergedSess);
      setEntries(r.data.entries || []);
      // Feb 24, 2026 (Randy — wrong-channel banner) — capture the
      // channel-match diagnostic so the workspace can render the
      // warning banner if this video has comments but zero from the
      // connected channel.
      setChannelMismatch(r.data?.channel_match || null);
      // Reset banner dismissal when a new URL loads — different video,
      // fresh decision to make.
      setMismatchBannerDismissed(false);
      setQuotaNotice(r.data?.channel_match?.quota_exceeded ? r.data.channel_match : null);
      // Iter160y (Randy pt-ytre-url-persist, Feb 2026) — DON'T clear the
      // URL input after a successful load. Randy: "Since this is a one
      // video shot. I would like that URL to stay server-side in the
      // text box instead of deleting." The server already persists the
      // URL on the session doc (video_url field), so keeping it in the
      // input just mirrors the loaded video and survives page refresh.
      setUrlInput(mergedSess?.video_url || url);
      setCurrentTime(0);
      setSaveFirstPrompt(null);
      // Refresh saved projects if we just saved one.
      if (opts.save === true) {
        const saved = await api.get("/ytre/sessions");
        setSavedProjects(saved.data?.projects || []);
      }
      toast.success("YTRE project loaded.");
      // Iter171 Ask 4 — refresh URL history after successful load
      if (siloId) {
        try {
          const h = await api.get(`/ytre/url-history?silo_id=${siloId}`);
          setUrlHistory(h.data?.history || []);
        } catch { /* silent */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load that video.");
    } finally {
      hintTimers.forEach(clearTimeout);
      setLoadStageHint("");
      setLoadingUrl(false);
    }
  }, [urlInput, siloId, session?.video_title]);

  // Feb 24, 2026 (Randy — dropdown delete) — remove one URL from the
  // recent-videos dropdown. Optimistic: strip from local state first,
  // then confirm with the backend. Falls back to a re-fetch on error
  // so the row reappears if the delete truly failed.
  const deleteHistoryRow = useCallback(async (historyId, evt) => {
    // Stop the row-body click (which would trigger loadUrl). Small
    // trash sits INSIDE the row button visually, so bubbling is real.
    evt?.stopPropagation?.();
    evt?.preventDefault?.();
    setUrlHistory((rows) => rows.filter((r) => r.id !== historyId));
    try {
      await api.delete(`/ytre/url-history/${historyId}`);
    } catch (err) {
      // Refetch to restore state on failure. Silent — no toast noise
      // for a niche delete failure.
      if (siloId) {
        try {
          const h = await api.get(`/ytre/url-history?silo_id=${siloId}`);
          setUrlHistory(h.data?.history || []);
        } catch { /* silent */ }
      }
    }
  }, [siloId]);

  const onUrlSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    // Iter160e (Randy) — SMART CTA on LOAD. Block the URL load if the
    // user hasn't connected YouTube for THIS silo yet. This paves the
    // way for the Wave 2 comment-preload flow (which also needs OAuth).
    try {
      const [siloRow, all] = await Promise.all([
        ytApi.getIntegration(siloId, "channel1").catch(() => null),
        ytApi.listMyIntegrations().catch(() => []),
      ]);
      const connectedHere = !!siloRow?.youtube_channel_id;
      if (!connectedHere) {
        // Not connected on this silo → show the gate CTA. If OAuth
        // exists elsewhere, the CTA will offer to mirror; otherwise
        // it opens the setup modal directly.
        setLoadGateAlternatives(all || []);
        setLoadGateOpen(true);
        return;
      }
    } catch { /* if the check itself errors, fall through — don't block */ }
    // If a working copy already exists, fire the save-first CTA.
    if (session) {
      setSaveFirstPrompt({ kind: "url", url: urlInput.trim() });
      return;
    }
    loadUrl();
  }, [session, urlInput, loadUrl, siloId]);

  // Iter172 (Randy pt-ytre-refresh-real, Feb 13, 2026 audit-2) — full
  // reconcile against YouTube. Calls the new backend
  // /refresh-from-youtube endpoint which re-fetches the video's comment
  // threads that involve the connected channel and:
  //   1. Adds any missing turns to existing families
  //   2. Creates full new families for threads never seen before
  //   3. Auto-drafts owner counter-replies for every viewer turn that
  //      still needs one
  // Zero effect on staged Mold reactions, edited body text, or Length
  // pills — reconciliation is additive only.
  // Sep 26, 2026 (Randy — production capture returned "(no captions
  // available for this segment)" on a video whose captions load fine in
  // preview). The transcript is fetched ONCE when the URL loads and cached
  // on the session; every capture only slices that cache. So a blocked
  // load-time fetch makes the capture button look broken forever. The
  // session now carries WHY, and this retries the fetch in place.
  // Sep 26, 2026 (Randy — "I'm not seeing toasts pop-up inside of the YTRE…
  // I was unable to see the reason why the YTRE didn't work"). Toasts are
  // hidden on phones platform-wide (his Jul 2026 call — they covered the macro
  // watch screen). YTRE is the one tab whose feedback IS the toast, so it
  // opts back in for as long as it is mounted. See index.css.
  useEffect(() => {
    if (!active) return undefined;
    document.body.classList.add("ytre-toasts");
    return () => document.body.classList.remove("ytre-toasts");
  }, [active]);

  // A loaded video with no cached transcript means every capture will be
  // empty, whether or not we have the reason on file. Sessions loaded before
  // Sep 26, 2026 have no `transcript_fetch_error`, so keying the banner on
  // that alone left exactly the production sessions Randy was staring at
  // with no banner at all.
  const transcriptMissing = !!session && !!session.video_id
    && (!!session.transcript_fetch_error || session.transcript_available === false);

  const retryTranscript = useCallback(async () => {
    if (!session?.id || retryingTranscript) return;
    setRetryingTranscript(true);
    try {
      const r = await api.post(`/ytre/sessions/${session.id}/transcript/retry`);
      if (r.data?.available) {
        setSession((s) => (s ? { ...s, transcript_available: true, transcript_fetch_error: null } : s));
        toast.success(`Transcript loaded · ${r.data.entry_count} caption lines. Recapture any empty cards.`);
      } else {
        setSession((s) => (s ? { ...s, transcript_fetch_error: r.data?.error || s.transcript_fetch_error } : s));
        toast.error(r.data?.error?.message || "Still no transcript for this video.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't retry the transcript fetch.");
    } finally {
      setRetryingTranscript(false);
    }
  }, [session?.id, retryingTranscript]);

  // Super-admin only (403s for everyone else). Runs BOTH transcript paths from
  // wherever the server actually is and prints what each one did — the only way
  // to tell a preview-vs-production difference apart from a bad video, since
  // neither path uses the YouTube API key.
  const runTranscriptProbe = useCallback(async () => {
    if (!session?.video_id) return;
    setTranscriptProbe({ loading: true });
    try {
      const r = await api.get("/ytre/admin/transcript-probe", {
        params: { video_id: session.video_id },
      });
      setTranscriptProbe(r.data);
    } catch (e) {
      setTranscriptProbe({ error: e.response?.data?.detail || e.message || "Probe failed." });
    }
  }, [session?.video_id]);

  const refreshEntries = useCallback(async () => {
    if (!session?.id || refreshingHistory) return;
    setRefreshingHistory(true);
    try {
      const r = await api.post(`/ytre/sessions/${session.id}/refresh-from-youtube`);
      if (r.data?.session) setSession(r.data.session);
      setEntries(r.data?.entries || []);
      // Feb 24, 2026 (Randy — wrong-channel banner) — refresh the
      // diagnostic every time. Reconnecting to the right channel will
      // flip `matched_threads` positive and dismiss the banner.
      setChannelMismatch(r.data?.channel_match || null);
      // Reset banner dismissal when a new URL loads — different video,
      // fresh decision to make.
      setMismatchBannerDismissed(false);
      setQuotaNotice(r.data?.channel_match?.quota_exceeded ? r.data.channel_match : null);
      const newTurns = r.data?.new_turns || 0;
      const newFams = r.data?.new_families || 0;
      const newPending = r.data?.new_drafts || 0;
      const total = newTurns + newFams + newPending;
      if (total === 0) {
        toast.success("Refresh complete · no new activity on YouTube.");
      } else {
        const bits = [];
        if (newFams) bits.push(`${newFams} new thread${newFams === 1 ? "" : "s"}`);
        if (newTurns) bits.push(`${newTurns} new turn${newTurns === 1 ? "" : "s"}`);
        if (newPending) bits.push(`${newPending} pending reply card${newPending === 1 ? "" : "s"}`);
        toast.success(`Refreshed from YouTube · ${bits.join(", ")}.`);
      }
    } catch (e) {
      toast.error(`Refresh failed: ${e?.response?.data?.detail || e?.message || "unknown"}`);
    } finally {
      setRefreshingHistory(false);
    }
  }, [session?.id, refreshingHistory]);

  // Sep 10 2026 (Randy: "simply navigating to the YTRE from a new session
  // ... should LOAD it all. right now its loading it wrong"). Tab mount
  // only ever read the saved canvas out of Mongo, so anything that had
  // happened on YouTube since the session was last touched was missing —
  // he landed on a single transcript and had to hit Refresh by hand.
  // Mount now runs the same reconcile the Refresh button does, once per
  // session, in the background: the saved canvas paints immediately and
  // is replaced when the reconcile lands. Silent by design — no toast,
  // and a failure just leaves the saved canvas on screen.
  const autoSyncedRef = useRef(new Set());
  const [autoSyncing, setAutoSyncing] = useState(false);
  useEffect(() => {
    if (!active || !session?.id || !session?.video_id || !session?.silo_id) return;
    if (autoSyncedRef.current.has(session.id)) return;
    autoSyncedRef.current.add(session.id);
    let cancelled = false;
    (async () => {
      setAutoSyncing(true);
      try {
        const r = await api.post(`/ytre/sessions/${session.id}/refresh-from-youtube`);
        if (cancelled) return;
        if (r.data?.session) {
          // Keep the user's pin preference — the mount effect already
          // reconciled it and the server copy may lag behind.
          setSession((s) => ({
            ...r.data.session,
            pin_video: typeof s?.pin_video === "boolean" ? s.pin_video : r.data.session.pin_video,
          }));
        }
        if (Array.isArray(r.data?.entries)) setEntries(r.data.entries);
        setChannelMismatch(r.data?.channel_match || null);
        setQuotaNotice(r.data?.channel_match?.quota_exceeded ? r.data.channel_match : null);
      } catch {
        /* silent — the saved canvas is already rendered */
      } finally {
        if (!cancelled) setAutoSyncing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active, session?.id, session?.video_id, session?.silo_id]);

  // Iter172 audit-3 (Randy pt-ytre-minimize-all, Feb 13, 2026) — was
  // "Minimize old" with a last-interacted exemption. Randy rethink:
  // too fiddly. NEW spec: minimize EVERY card, full stop. Glow keys
  // on pending-Approve state (separate concern), not on which card
  // was touched last, so no tracking needed here.
  // Sep 10 2026 (Randy) — now a toggle: Minimize all ⇄ Open all. "Open
  // all" lands on the MIDDLE stage, not fully open.
  const runMinimizeAll = useCallback(() => {
    if (!entries.length) return;
    const opening = allMinimized;
    setMinimizeStage(opening ? 1 : 2);
    setMinimizeTick((t) => t + 1);
    setAllMinimized(!opening);
    toast.success(opening
      ? `All ${entries.length} cards opened.`
      : `All ${entries.length} cards minimized.`);
  }, [entries, allMinimized]);

  // Iter172 audit-7 (Randy pt-ytre-canvas-search, Feb 17, 2026).
  // Recompute canvas matches whenever entries or the query change.
  // Order matches by canvas position — `entries` is already sorted
  // by order_index at render, so we feed that ordering through.
  const searchMatches = useMemo(() => {
    if (!searchQuery) return [];
    return computeCanvasMatches(entries, searchQuery);
  }, [entries, searchQuery]);

  // When query changes, reset the active index and target the first
  // match's entry for reveal + scroll.
  useEffect(() => {
    setActiveMatchIdx(0);
    if (searchMatches.length > 0) {
      const first = searchMatches[0];
      setRevealTargetId(first.entryId);
      setRevealNonce((n) => n + 1);
    } else {
      setRevealTargetId(null);
    }
  }, [searchQuery]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Next/prev cycle. Wraps around; bumps searchWrapPulseTick on wrap
  // so the counter briefly pulses (mobile-friendly cue).
  const gotoMatch = useCallback((newIdx, wrapped) => {
    if (searchMatches.length === 0) return;
    const clamped = ((newIdx % searchMatches.length) + searchMatches.length) % searchMatches.length;
    const target = searchMatches[clamped];
    setActiveMatchIdx(clamped);
    setRevealTargetId(target.entryId);
    setRevealNonce((n) => n + 1);
    if (wrapped) setSearchWrapPulseTick((t) => t + 1);
  }, [searchMatches]);

  const onSearchNext = useCallback(() => {
    if (searchMatches.length === 0) return;
    const next = activeMatchIdx + 1;
    const wrapped = next >= searchMatches.length;
    gotoMatch(wrapped ? 0 : next, wrapped);
  }, [activeMatchIdx, searchMatches, gotoMatch]);

  const onSearchPrev = useCallback(() => {
    if (searchMatches.length === 0) return;
    const prev = activeMatchIdx - 1;
    const wrapped = prev < 0;
    gotoMatch(wrapped ? searchMatches.length - 1 : prev, wrapped);
  }, [activeMatchIdx, searchMatches, gotoMatch]);

  // Scroll the target card into view whenever revealNonce bumps.
  // (Also the cards themselves handle the chevron reveal.)
  useEffect(() => {
    if (revealNonce === 0 || !revealTargetId) return;
    // Wait one tick so cards react to the reveal signal first.
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-ytre-entry-id="${revealTargetId}"]`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 60);
    return () => clearTimeout(t);
  }, [revealNonce, revealTargetId]);

  // Build a per-entry map from match records so cards can quickly
  // find (a) whether they have any matches (b) which occurrence is
  // the active one within THEIR text.
  const perEntrySearchInfo = useMemo(() => {
    const info = {};
    if (!searchMatches.length) return info;
    searchMatches.forEach((m, i) => {
      if (!info[m.entryId]) info[m.entryId] = { occurrences: 0, activeOccIdx: null };
      info[m.entryId].occurrences += 1;
      if (i === activeMatchIdx) info[m.entryId].activeOccIdx = m.occIdx;
    });
    return info;
  }, [searchMatches, activeMatchIdx]);




  const capture = useCallback(async () => {
    if (!session) return;
    if (currentTime <= 0) { toast.error("Play the video a bit before capturing."); return; }
    setCapturing(true);
    try {
      const r = await api.post(`/ytre/sessions/${session.id}/capture`, {
        current_time_sec: currentTime,
        minutes_prior: effectiveMinutesPrior,
      });
      // Iter160g (Randy Wave-2 correction) — the backend re-sorts every
      // entry in the session by video-timestamp order after inserting.
      // A capture at 15:00 must slot ABOVE a prior capture at 30:00 —
      // so we can't just append the new entry to local state, we need
      // the canonical server-sorted list. Cheap re-fetch, one round-trip.
      const snap = await api.get(`/ytre/sessions/${session.id}`);
      setEntries(snap.data.entries || []);
      setSession((s) => (s ? { ...s, last_injection_at_sec: r.data.entry.time_end_sec } : s));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Capture failed.");
    } finally {
      setCapturing(false);
    }
  }, [session, currentTime, effectiveMinutesPrior]);

  // `C` keyboard shortcut for capture. Skipped when a text input is focused
  // so typing 'c' in the URL bar / note editor doesn't fire the button.
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key !== "c" && e.key !== "C") return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      capture();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, capture]);

  const runOpOnEntry = async (entryId, commandId, commandName) => {
    if (!session) return;
    setBusyEntryId(entryId);
    try {
      const r = await api.post(`/ytre/sessions/${session.id}/entries/${entryId}/op`, {
        command_id: commandId,
        command_name: commandName,
      });
      setEntries((prev) => prev.map((e) => (e.id === entryId ? r.data.entry : e)));
      toast.success("Operation applied.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Operation failed.");
    } finally {
      setBusyEntryId(null);
    }
  };

  // See the module-scope `friendlyPipelineError` helper at the top of this
  // file — it's shared with the MasterReplyModal below.

  const askSilo = async (sourceEntryId, nudgeCommandId, nudgeLabel, length = null, moldingHint = null) => {
    if (!session) return;
    setBusyEntryId(sourceEntryId);
    try {
      const body = { source_entry_id: sourceEntryId, nudge_command_id: nudgeCommandId };
      if (length) body.length = length;
      // Feb 28 2026 (Randy pt-ytre-react-two-stage) — thread first-
      // response guidance from the pad's "Guide this reply" action.
      // Randy: "the inability to see that pop-up on your first reply
      // ... needs to be considered ... turn the initial 'React to
      // transcript' button into a two stage button." Same
      // molding_hint plumbing the redraft flow uses — backend
      // handler appends this to the assembled question as
      // ADDITIONAL OWNER GUIDANCE.
      if (moldingHint && String(moldingHint).trim()) body.molding_hint = String(moldingHint).trim();
      // Iter160e — session LLM override piggybacks on every /ask-silo call.
      // Wiped on tab refresh; see /app/frontend/src/lib/sessionLlm.js.
      Object.assign(body, overrideAsAskBody("public_ask"));
      await runYtreAsyncJob(`/ytre/sessions/${session.id}/ask-silo`, body);
      // Iter160h (Randy pt-ytre-p2 followup) — the backend now reorders
      // every entry after ask-silo insert (so the response slots next
      // to its source transcript). Frontend must re-fetch the whole
      // session snapshot to reflect the canonical order — appending
      // to local state was pinning new responses to the bottom.
      const snap = await api.get(`/ytre/sessions/${session.id}`);
      setEntries(snap.data.entries || []);
      toast.success(`Silo reacted (${nudgeLabel || "nudge"}).`);
    } catch (e) {
      toast.error(friendlyPipelineError(e, "React to transcript failed."));
    } finally {
      setBusyEntryId(null);
    }
  };

  // Sep 29 2026 (Randy pt-ytre-ignore-placeholder) — park a `Pending reply`
  // placeholder as "Ignore" (or put it back). The backend also retires that
  // thread's share of the video's unread counter, so the alert bubble it was
  // raising doesn't come back forever.
  const ignoreEntry = async (entryId, ignored) => {
    if (!session) return;
    try {
      const r = await api.post(`/ytre/sessions/${session.id}/entries/${entryId}/ignore`, { ignored });
      const updated = r.data?.entry;
      setEntries((prev) => prev.map((e) => {
        if (e.id === entryId) return updated || e;
        // The glow the backend just cleared on this thread's sibling cards.
        if (ignored && r.data?.glow_cleared && updated?.youtube_thread_id
            && e.youtube_thread_id === updated.youtube_thread_id) {
          return { ...e, is_new_inbound_reply: false };
        }
        return e;
      }));
      if (ignored) {
        toast.success(r.data?.alert_cleared
          ? `Ignored — retired ${r.data.alert_cleared} new-reply alert${r.data.alert_cleared === 1 ? "" : "s"} on that thread.`
          : "Ignored — this card no longer counts as waiting on you.");
      } else {
        toast.success("Back to Pending reply.");
      }
      try {
        const a = await api.get(`/ytre/silo/${siloId}/activity`);
        setActivityAlerts(a.data?.unread_urls || []);
      } catch { /* the banner just keeps its last state */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update that card.");
    }
  };

  // Sep 29, 2026 (Randy pt-voice-kit) — after a substantial hand-edit, offer
  // to flag the card as "100% my words" so the Voice Kit may use it as a
  // specimen. Light touch-ups pass silently (see ASK_THRESHOLD).
  const [voiceAsk, setVoiceAsk] = useState(null); // {entryId, ratio}

  const saveEntryEdit = async (entryId, patch) => {
    if (!session) return;
    const before = entries.find((e) => e.id === entryId);
    try {
      const r = await api.patch(`/ytre/sessions/${session.id}/entries/${entryId}`, patch);
      setEntries((prev) => prev.map((e) => (e.id === entryId ? r.data.entry : e)));
      const field = ["transcript_revised", "transcript_raw", "silo_response_text"].find((f) => patch[f] !== undefined);
      if (field && before && !before.voice_original) {
        const ratio = editRatio(before[field] || before.transcript_raw || "", patch[field] || "");
        if (ratio >= ASK_THRESHOLD) setVoiceAsk({ entryId, ratio, text: patch[field] || "" });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    }
  };

  const decideVoiceOriginal = async (flag) => {
    const ask = voiceAsk;
    setVoiceAsk(null);
    if (!ask || flag === null || !session) return;
    try {
      const r = await api.patch(`/ytre/sessions/${session.id}/entries/${ask.entryId}`, { voice_original: !!flag });
      setEntries((prev) => prev.map((e) => (e.id === ask.entryId ? r.data.entry : e)));
      if (flag) toast.success("Flagged — the Voice Kit can now pull this reply as a writing sample.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't flag that reply.");
    }
  };

  // Sep 10 2026 (Randy: "I got no popup though when I chose to delete it").
  // This used to be a native window.confirm. Chrome suppresses repeat native
  // dialogs from the same page (and then returns false), so after a few
  // deletes the warning silently stopped appearing — on a destructive action
  // that is now PERMANENT. In-app dialog instead: always renders, and it can
  // actually say what is about to be forgotten.
  const [pendingDelete, setPendingDelete] = useState(null); // {entryId, childCount, kind, label}

  const deleteEntry = async (entryId) => {
    if (!session) return;
    const entry = entries.find((e) => e.id === entryId);
    let childCount = 0;
    try {
      const r = await api.get(`/ytre/sessions/${session.id}/entries/${entryId}/child-count`);
      childCount = Number(r.data?.count || 0);
    } catch (e) {
      childCount = 0;
    }
    setPendingDelete({
      entryId,
      childCount,
      kind: entry?.kind || "card",
      label: entry?.kind === "transcript"
        ? `Transcript · ${fmtTime(entry?.time_start_sec)} – ${fmtTime(entry?.time_end_sec)}`
        : "this card",
      isCapture: Boolean(entry?.kind === "transcript"
        && Number.isInteger(entry?.time_end_sec)
        && Number.isInteger(entry?.time_start_sec)
        && entry.time_end_sec > entry.time_start_sec),
    });
  };

  const confirmDelete = async () => {
    const p = pendingDelete;
    setPendingDelete(null);
    if (!p || !session) return;
    try {
      const r = await api.delete(`/ytre/sessions/${session.id}/entries/${p.entryId}`);
      const cascaded = Number(r.data?.cascade_deleted || 0);
      const forgot = r.data?.forgot || {};
      setEntries((prev) => prev.filter((e) => e.id !== p.entryId && e.source_entry_id !== p.entryId));
      const forgotBits = [
        forgot.capture_points ? "capture point" : null,
        forgot.threads ? "thread" : null,
        forgot.comments ? `${forgot.comments} comment${forgot.comments === 1 ? "" : "s"}` : null,
      ].filter(Boolean);
      if (cascaded > 0) {
        toast.success(`Card + ${cascaded} child card${cascaded === 1 ? "" : "s"} deleted.${forgotBits.length ? ` Won't come back (${forgotBits.join(", ")} forgotten).` : ""}`);
      } else if (forgotBits.length) {
        toast.success(`Deleted — and forgotten (${forgotBits.join(", ")}). It won't reload.`);
      } else {
        toast.success("Deleted.");
      }
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  // Iter160f (Randy Wave-2) — pin an entry to a new video timestamp
  // and let the backend auto-resort every card in the session. Used
  // by both TranscriptCard (which re-slices the transcript for the
  // new window) and UserCommentCard (which just moves the card to
  // its new position). Either way the response includes ALL entries'
  // updated order_index values via a fresh session fetch — cheaper
  // than a bespoke sort-only endpoint.
  const recaptureEntry = async (entryId, startSeconds, endSeconds) => {
    if (!session) throw new Error("No active session");
    // Feb 2026 (Randy pt-ytre-recapture-range · iter169-Phase2) — accepts
    // explicit start+end range. Legacy signature (single timestamp) still
    // supported: if only one arg is passed, treat it as the end and use
    // a 5-min prior default. Callers moved to the new range shape via
    // the RecaptureButton overhaul.
    let body;
    if (typeof endSeconds === "number") {
      body = { start_seconds: startSeconds, end_seconds: endSeconds };
    } else {
      // Legacy single-point call — synthesize a 5-min prior window.
      body = { timestamp_seconds: startSeconds };
    }
    const r = await api.post(
      `/ytre/sessions/${session.id}/entries/${entryId}/recapture`,
      body,
    );
    // Re-fetch the session so every entry's order_index reflects the
    // canonical server-side sort. Cheap — a session snapshot is small.
    // Sep 10 2026 (Randy) — a transcript recapture now reorders on the
    // server, so this snapshot is what makes the card (and the comments
    // under it) slide into place with no refresh or reload.
    const snap = await api.get(`/ytre/sessions/${session.id}`);
    setEntries(snap.data.entries || []);
    toast.success("Transcript re-pulled · card moved into place.");
    return r.data.entry;
  };

  // Iter170 §4-5 (Randy pt-ytre-approve-post, Feb 2026) — Approve a
  // draft owner comment card and post it to YouTube via the silo's
  // connected channel. The reply lands as a real posted comment on
  // YT (parentId = family's top-level YouTube comment id, cached on
  // the entry when it was auto-drafted). On success flips the card
  // to post_state="posted" so the footer swaps to the Approved pill.
  const approveAndPost = async (entryId) => {
    if (!session) return;
    try {
      const r = await api.post(
        `/ytre/sessions/${session.id}/entries/${entryId}/approve-and-post`,
      );
      const updated = r.data?.entry;
      if (updated) {
        setEntries((prev) => prev.map((e) => (e.id === entryId ? updated : e)));
        toast.success("Reply posted to YouTube.");
      } else {
        toast.success("Reply posted to YouTube.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't post reply to YouTube.");
    }
  };

  // Feb 26, 2026 (Randy pt-ytre-silo-response-post) — post a silo_response
  // card (parent card in the capture workflow) directly to YouTube as a
  // new top-level comment on the video. Mirrors the child user_comment
  // approve-and-post flow but goes via `/sessions/{id}/post` (mode "new",
  // entry_id set) so we get commentThreads.insert instead of the child
  // path's comments.insert(parentId=...). On success the backend stamps
  // post_state="posted" on the entry so the button flips to an
  // "Approved" pill.
  const postSiloResponse = async (entryId, bodyText, opts = {}) => {
    if (!session) return;
    const clean = (bodyText || "").trim();
    if (!clean) {
      toast.error("Nothing to post — this card is empty.");
      return;
    }
    try {
      // Feb 28 2026 (Randy pt-ytre-silo-response-repost) — support
      // Re-post as an UPDATE on the entry's existing YT comment id
      // when the card is already posted + edited/re-drafted. Backend
      // reads the entry's `youtube_comment_id` server-side; frontend
      // just picks the mode.
      const mode = opts.mode === "update" ? "update" : "new";
      const r = await api.post(`/ytre/sessions/${session.id}/post`, {
        comment_text: clean,
        mode,
        entry_id: entryId,
      });
      const updated = r.data?.entry;
      if (updated) {
        setEntries((prev) => prev.map((e) => (e.id === entryId ? updated : e)));
      }
      toast.success(mode === "update" ? "Comment updated on YouTube." : "Comment posted to YouTube.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't post comment to YouTube.");
    }
  };

  // Iter170 §4-6 (Randy pt-ytre-redraft, Feb 2026) — regenerate the
  // body text of an owner comment card through the YTRE special
  // pipeline (Dialogue > Silo > Transcript). Called from the Re-draft
  // assist modal's "Re-draft now" submit. If molding_hint is non-empty,
  // it's appended to the prompt scaffold as owner guidance. length_pref
  // reflects the card's OWN Length pill (per-card, independent).
  const redraftOwnerCard = async (entryId, payload = {}) => {
    if (!session) return;
    try {
      // Feb 27 2026 (Randy pt-ytre-guide-this-reply) — thread the
      // one-shot guidance (if any) into `molding_hint`. Backend
      // already accepts this field on /redraft.
      //
      // Feb 27 2026 revised (pt-ytre-guide-persist) — guidance is
      // now `{ text, persist }`; clear only when NOT sticky.
      const g = guidanceByEntry[entryId];
      const body = { ...(payload || {}) };
      if (g?.text) body.molding_hint = g.text;
      if (g && !g.persist) {
        setGuidanceByEntry((m) => {
          const next = { ...m };
          delete next[entryId];
          return next;
        });
      }
      // Feb 2026 (Randy pt-ytre-async-jobs) — endpoint now returns
      // {job_id}; helper polls and returns the same {entry} payload
      // the sync endpoint used to.
      const result = await runYtreAsyncJob(
        `/ytre/sessions/${session.id}/entries/${entryId}/redraft`,
        body,
      );
      const updated = result?.entry;
      if (updated) {
        setEntries((prev) => prev.map((e) => (e.id === entryId ? updated : e)));
        toast.success(updated.post_state === "draft"
          ? "Re-drafted — review + Approve when ready."
          : "Re-drafted.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't re-draft this card.");
    }
  };

  // Feb 2026 (Randy pt-ytre-clear) — hard-reset the YTRE canvas. Wipes the
  // active session (working copy) and its entries. Saved projects in the
  // bottom strip are NOT touched. Prompts a confirm so an accidental tap
  // can't nuke unsaved work. If no session is loaded, this is a no-op.
  const clearCanvas = async () => {
    if (!session) return;
    const ok = window.confirm(
      "Clear the YTRE canvas? This wipes the current project (working copy) and all its entries. Saved projects in the bottom strip are preserved."
    );
    if (!ok) return;
    try {
      await api.delete(`/ytre/sessions/${session.id}`);
      setSession(null);
      setEntries([]);
      setCurrentTime(0);
      setUrlInput("");
      setSaveFirstPrompt(null);
      toast.success("Canvas cleared.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clear the canvas.");
    }
  };

  const togglePinVideo = async () => {
    if (!session) return;
    const nextVal = !session.pin_video;
    setSession((s) => (s ? { ...s, pin_video: nextVal } : s));
    try {
      await api.patch(`/ytre/sessions/${session.id}`, { pin_video: nextVal });
    } catch { /* silent — the toggle already flipped visually */ }
    // Feb 2026 (Randy pt-ytre-pin-persist) — mirror the choice to the user's
    // per-silo ui_prefs so future sessions on THIS silo (a new URL load
    // creates a fresh ytre_sessions row) inherit the last choice. Without
    // this, every fresh video load reset pin_video back to `True`.
    if (siloId) {
      try { await api.patch("/me/ui-prefs", { silo_id: siloId, key: "ytre_pin_video", value: nextVal }); } catch { /* silent */ }
    }
  };

  // Iter160y (Randy pt-ytre-load-flow-fix, Feb 2026) — reworked to use
  // the new backend clone endpoint. Randy audit: the prior local-only
  // load left the server-side row flagged is_working_copy=false, so
  // subsequent "Discard" taps deleted the saved project itself. New
  // shape: POST /sessions/{id}/load returns a fresh working-copy CLONE
  // with new UUIDs; the original saved project stays exactly where it
  // was in the strip.
  //
  // Second-pass args (`saveFirst`) fire from the shared save-first
  // modal's three-button CTA:
  //   null      → first tap; if a working copy exists, defer to modal
  //   true      → save current working copy first, then load
  //   false     → discard current working copy, then load
  const loadSavedProject = async (projectId, saveFirst = null) => {
    if (saveFirst === null && session) {
      setSaveFirstPrompt({ kind: "project", projectId });
      return;
    }
    try {
      const r = await api.post(`/ytre/sessions/${projectId}/load`, {
        save_current_working_copy: saveFirst === true,
        save_title: saveFirst === true
          ? (session?.video_title || "Untitled project")
          : undefined,
      });
      setSession(r.data.session);
      setEntries(r.data.entries || []);
      setUrlInput(r.data.session?.video_url || "");
      setSaveFirstPrompt(null);
      // Refresh the saved-projects strip — the working copy we just
      // promoted (if saveFirst=true) needs to appear as a new tile.
      if (saveFirst === true) {
        const saved = await api.get("/ytre/sessions");
        setSavedProjects(saved.data?.projects || []);
      }
      toast.success("Project loaded.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load that project.");
    }
  };

  // Iter160h (Randy pt-ytre-p2 followup) — permanently delete a saved
  // project. Server also cascades every entry attached to the session.
  // Confirm dialog first — this can't be undone.
  const deleteSavedProject = async (project) => {
    const title = project.project_title || project.video_title || "this project";
    if (!window.confirm(`Delete "${title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/ytre/sessions/${project.id}`);
      setSavedProjects((prev) => prev.filter((p) => p.id !== project.id));
      toast.success("Project deleted.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete that project.");
    }
  };

  const saveCurrentProject = async () => {
    if (!session) return;
    const title = window.prompt("Name this project:", session.video_title || "Untitled project");
    if (!title) return;
    try {
      await api.patch(`/ytre/sessions/${session.id}`, {});
      // Backend endpoint that promotes wc→saved-project runs inside from-url
      // when save_current_working_copy=True is passed. To save-in-place
      // (without loading a new URL), we just flip is_working_copy via a
      // targeted PATCH. Falling back to the from-url flow when the user
      // paste a new URL is the primary save path.
      // For now: mark it saved by re-issuing from-url with the current URL
      // so backend cleanly promotes → saves → creates a fresh wc.
      const r = await api.post("/ytre/sessions/from-url", {
        video_url: session.video_url,
        silo_id: session.silo_id,
        save_current_working_copy: true,
        save_title: title,
      });
      setSession(r.data.session);
      setEntries(r.data.entries || []);
      const saved = await api.get("/ytre/sessions");
      setSavedProjects(saved.data?.projects || []);
      toast.success("Project saved to the bottom strip.");
    } catch (e) {
      toast.error("Save failed.");
    }
  };

  const discardCurrentProject = async () => {
    if (!session) return;
    if (!window.confirm("Discard this project? All captures, silo responses, and notes will be lost.")) return;
    try {
      await api.delete(`/ytre/sessions/${session.id}`);
      setSession(null);
      setEntries([]);
      setCurrentTime(0);
      toast.success("Project discarded.");
    } catch { toast.error("Discard failed."); }
  };

  // ─────────────────────────────────────────────────────
  // Master Reply modal + redraft-with-capture-pad plumbing.
  // Iter160b — reuses the YT Replies pipeline patterns:
  //   • Redraft opens the same YTRedraftCapturePad drawer that arms the
  //     button (armed = green, "Re-draft now" label).
  //   • Length dropdown is staging-only — pick then Generate/Re-draft.
  //   • Editable body via <textarea>; PATCH persists.
  //   • Send-to-silo routes through the shared DocSaveDialog.
  // ─────────────────────────────────────────────────────
  const [masterOpen, setMasterOpen] = useState(false);
  // Iter160e (Randy) — Smart CTA that gates the URL LOAD when this
  // silo has no YouTube channel connected yet. Offers to mirror from
  // another silo/module if the user has any OAuth elsewhere.
  const [loadGateOpen, setLoadGateOpen] = useState(false);
  // Sep 14 2026 (Randy — channel switching from the alert row). Which hat is
  // bound to this silo right now, so a row whose conversation belongs to a
  // different channel can offer the switch before loading.
  const [boundChannel, setBoundChannel] = useState(null);
  const [switchRow, setSwitchRow] = useState(null);
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const row = await ytApi.getIntegration(siloId, "channel1");
        if (!dead) setBoundChannel(row || null);
      } catch { if (!dead) setBoundChannel(null); }
    })();
    return () => { dead = true; };
  }, [siloId]);
  // Sep 16 2026 (Randy) — ONE definition of "wrong hat", shared by the
  // activity-row switch prompt and the wrong-channel banner. They used to
  // disagree: the banner fired only when ZERO threads matched the connected
  // channel, which measures PARTICIPATION. On his Christian-Vs-Muslim video
  // @EastTactics appears as a replier, so matched_threads was positive and
  // the banner stayed silent — even though @cosmicequivalence owns the
  // conversation and any reply he posted would go out under the wrong name.
  // Ownership is the thing that matters, so ownership is what we test.
  const _hatMismatch = (want, have) => !!(want && have && want !== have);
  const _switchNeeded = (row) => _hatMismatch(
    row?.interaction_channel_id || row?.unread_activity?.interaction_channel_id,
    boundChannel?.youtube_channel_id,
  );
  // Wrong hat OR none of his hats at all — two genuinely different
  // situations, one banner, different copy for each.
  const _wrongHat = (cm) => (
    _hatMismatch(cm?.interaction_channel_id, cm?.connected_channel_id)
    || ((cm?.total_threads || 0) > 0 && (cm?.matched_threads || 0) === 0)
  );
  // Sep 16 2026 — dismissal is keyed PER VIDEO. It used to be keyed on the
  // connected channel alone, so dismissing it once hid the banner on every
  // video for as long as that channel stayed connected.
  const _mismatchKey = (cm, sess) =>
    `mysilo:ytre:mismatch-dismissed:${sess?.video_id || "none"}:${cm?.connected_channel_id || "none"}`;
  const [loadGateAlternatives, setLoadGateAlternatives] = useState([]);
  // Iter160e (Randy) — SESSION-ONLY LLM surface picker for the /ask-silo
  // loopback. The pick is remembered as long as this tab stays open;
  // refresh reverts to the platform/silo cascade. Model label rendered
  // on the button label so users see which model they've staged.
  const [llmPickerOpen, setLlmPickerOpen] = useState(false);
  const [llmOverrideTick, setLlmOverrideTick] = useState(0);
  const activeOverride = useMemo(() => readSessionLlmOverride("public_ask"), [llmOverrideTick]);
  // Iter160c — custom nudge builder + two-sided export DocSaveDialog.
  const [customNudgeOpen, setCustomNudgeOpen] = useState(false);
  const [customNudgeEdit, setCustomNudgeEdit] = useState(null);
  const [opsModalOpen, setOpsModalOpen] = useState(false);
  const [twoSidedSiloDialogOpen, setTwoSidedSiloDialogOpen] = useState(false);
  const [twoSidedContent, setTwoSidedContent] = useState("");
  const openMasterReply = () => {
    if (!session) return;
    if (!session.silo_id) return toast.error("Link this project to a silo first.");
    if (entries.length === 0) return toast.error("Capture at least one transcript slice before compiling.");
    setMasterOpen(true);
  };

  // ─────────────────────────────────────────────────────
  // Two-sided source export — Iter160c. Compiles the raw workspace
  // (transcript slices + silo answers, chronologically) into a Markdown
  // block and offers three destinations (Copy / Send to Silo via
  // DocSaveDialog / Send to Workbench). Endpoints already exist.
  // ─────────────────────────────────────────────────────
  const fetchTwoSided = async () => {
    const r = await api.get(`/ytre/sessions/${session.id}/export/two-sided?format=md`);
    return r.data?.content || "";
  };

  const copyTwoSided = async () => {
    if (!session || entries.length === 0) return toast.error("Nothing to copy yet.");
    try {
      const content = await fetchTwoSided();
      if (!content) return toast.error("Two-sided source came back empty.");
      await navigator.clipboard.writeText(content);
      toast.success("Two-sided source copied.");
    } catch (e) { toast.error(e.response?.data?.detail || "Copy failed."); }
  };

  const sendTwoSidedToWorkbench = async () => {
    if (!session || entries.length === 0) return toast.error("Nothing to send yet.");
    try {
      // Iter160r (Randy pt-ytre-send-workbench-realtime, Feb 2026) —
      // append to Workbench staging server-side, then sync the new
      // staging text into sessionStorage + fire the `staging-updated`
      // event Workbench already listens for. Previously the Workbench
      // tab required a full page refresh to see the appended content.
      const r = await api.post(`/ytre/sessions/${session.id}/export/to-workbench`, {
        kind: "two-sided",
      });
      const nextStaging = r.data?.staging;
      const targetSilo = r.data?.silo_id || siloId;
      if (typeof nextStaging === "string" && targetSilo) {
        try { writeStaging(targetSilo, nextStaging); } catch { /* */ }
        try {
          window.dispatchEvent(new CustomEvent("staging-updated", { detail: { siloId: targetSilo } }));
        } catch { /* */ }
      }
      toast.success(`Two-sided source sent to Workbench (+${(r.data?.appended_chars || 0).toLocaleString()} chars).`);
    } catch (e) { toast.error(e.response?.data?.detail || "Send to Workbench failed."); }
  };

  // When the Send-to-Silo dialog opens for two-sided export, pre-fetch
  // the content so DocSaveDialog has it ready.
  useEffect(() => {
    if (!twoSidedSiloDialogOpen || !session) return;
    let cancelled = false;
    fetchTwoSided().then((c) => { if (!cancelled) setTwoSidedContent(c); }).catch(() => {});
    return () => { cancelled = true; };
  }, [twoSidedSiloDialogOpen, session?.id]);

  // Delete a user-owned custom nudge from the AskSiloDropdown's inline
  // trash icon. Confirms first — otherwise a mis-tap wipes work.
  const deleteCustomNudge = async (commandId, label) => {
    if (!window.confirm(`Delete custom nudge "${label}"? This can't be undone.`)) return;
    try {
      await api.delete(`/commands/${commandId}`);
      setNudges((prev) => prev.filter((n) => n.id !== commandId));
      toast.success("Custom nudge deleted.");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed."); }
  };

  // Called by the CustomNudgeModal on successful save — appends the new
  // nudge to the dropdown so the user can use it immediately, or swaps the
  // edited one in place (Jun 2026, Randy: delete was there, edit was not).
  const onCustomNudgeCreated = (saved) => {
    setNudges((prev) => (prev.some((n) => n.id === saved.id)
      ? prev.map((n) => (n.id === saved.id ? saved : n))
      : [...prev, saved]));
    setCustomNudgeOpen(false);
    setCustomNudgeEdit(null);
  };

  // Operations are the Workbench pool (scope=slots) — refresh after the shared
  // Custom-commands dialog writes one, so the menu shows it immediately.
  const reloadOps = async () => {
    try {
      const r = await api.get("/commands?scope=slots");
      setCommands(r.data || []);
    } catch { /* the menu keeps whatever it already had */ }
  };

  const editCustomNudge = (commandId) => {
    const n = nudges.find((x) => x.id === commandId);
    if (!n) return;
    setCustomNudgeEdit(n);
    setCustomNudgeOpen(true);
  };

  // Per-transcript-op re-draft state:
  //   busyEntryId → the entry currently getting an LLM call
  //   redraftArmed → { [entryId]: true } means capture-pad was opened;
  //     next tap on Re-draft actually re-runs the op.
  //   redraftPad → { entryId } | null — which entry's capture pad is open.
  //   guidanceByEntry → { [entryId]: { text, persist } } — guidance
  //     from "Guide this reply". `persist=true` keeps it armed for
  //     future re-drafts of THAT card; `persist=false` (default) is
  //     one-shot and clears after next consumption. Randy's revised
  //     ask: chip + sticky toggle, not invisible. Never persisted
  //     server-side, never written to silo memory.
  const [redraftArmed, setRedraftArmed] = useState({});
  const [redraftPad, setRedraftPad] = useState(null);
  const [guidanceByEntry, setGuidanceByEntry] = useState({});
  const openRedraftPad = (entryId) => setRedraftPad({ entryId });
  const closeRedraftPad = (_didPush, guidance, persist) => {
    if (redraftPad?.entryId) {
      setRedraftArmed((m) => ({ ...m, [redraftPad.entryId]: true }));
      if (guidance && guidance.trim()) {
        setGuidanceByEntry((m) => ({
          ...m,
          [redraftPad.entryId]: { text: guidance.trim(), persist: !!persist },
        }));
      } else {
        toast.success("Silo updated — tap Re-draft again to regenerate.");
      }
    }
    setRedraftPad(null);
  };
  const clearGuidance = (entryId) => {
    setGuidanceByEntry((m) => {
      const next = { ...m };
      delete next[entryId];
      return next;
    });
  };

  // Re-ask the silo on a source transcript entry using the FAMILY's
  // currently-staged nudge + length (Iter160s, Randy pt-ytre-family-
  // mirror). Deletes the old response and appends a fresh one so
  // chronology stays sane. If the family somehow has no staged nudge
  // (shouldn't happen — the button is locked until it does), we fall
  // back to the response's ORIGINAL nudge so we never fire an
  // unattributed call.
  const redraftSiloResponse = async (responseEntry, length = null, cardNudge = null, cardTone = null) => {
    if (!responseEntry.source_entry_id) {
      toast.error("Missing source anchor for this response — can't re-draft.");
      return;
    }
    // Iter172 (Randy pt-ytre-per-card-mold, Feb 13, 2026) — the
    // card's LOCAL staged Mold reaction is now the source of truth,
    // no family-mirror. Falls back to the card's originally-recorded
    // nudge_command_id (the one that produced this silo_response the
    // first time) if the user hasn't picked a new one locally.
    const effectiveNudgeId = cardNudge?.id || responseEntry.nudge_command_id;
    if (!effectiveNudgeId) {
      toast.error("Pick a Mold Silo Reaction from this card's footer first.");
      return;
    }
    setBusyEntryId(responseEntry.id);
    try {
      // Feb 28 2026 (Randy pt-ytre-silo-response-repost) — server-
      // side atomic swap: pass `redraft_entry_id`; backend creates
      // the new response, carries over the OLD row's YT metadata
      // (if the old was posted), then deletes the old. Removes the
      // client-side "delete-then-ask" race that used to lose the
      // posted metadata.
      const body = {
        source_entry_id: responseEntry.source_entry_id,
        nudge_command_id: effectiveNudgeId,
        redraft_entry_id: responseEntry.id,
      };
      if (length) body.length = length;
      // Iter172 (Randy pt-ytre-tone-picker, Feb 13, 2026) — send
      // the card's staged Tone id if the user picked one. Backend
      // /ask-silo injects the tone directive into the combined
      // prompt right after the length directive. Null / omitted →
      // silo uses its natural voice (default off).
      if (cardTone?.id) body.tone_id = cardTone.id;
      // Feb 27 2026 (Randy pt-ytre-guide-this-reply) — thread the
      // guidance from "Guide this reply" into the ask-silo payload.
      // Sticky guidance is preserved; one-shot clears after use.
      const g = guidanceByEntry[responseEntry.id];
      if (g?.text) {
        body.molding_hint = g.text;
        if (!g.persist) {
          setGuidanceByEntry((m) => {
            const next = { ...m };
            delete next[responseEntry.id];
            return next;
          });
        }
      }
      Object.assign(body, overrideAsAskBody("public_ask"));
      await runYtreAsyncJob(`/ytre/sessions/${session.id}/ask-silo`, body);
      const snap = await api.get(`/ytre/sessions/${session.id}`);
      setEntries(snap.data.entries || []);
      setRedraftArmed((m) => ({ ...m, [responseEntry.id]: false }));
      toast.success("Silo comment re-drafted.");
    } catch (e) {
      toast.error(friendlyPipelineError(e, "Re-draft failed."));
    } finally {
      setBusyEntryId(null);
    }
  };

  // Iter160r — Randy audit fix. An existing YouTube comment (kind =
  // user_comment) IS the equivalent of a silo answer to its paired
  // transcript slice, so the card gets its own Re-draft footer (Nudge +
  // Length + Execute). Iter160s update — this now uses FAMILY state
  // (mirrored across the whole transcript group) rather than the
  // card's local state. Firing it stacks a FRESH silo_comment below,
  // using the user comment's `source_entry_id` (the paired transcript
  // slice) as the ask-silo source. The original user_comment stays
  // intact — this generates an ALTERNATIVE, not an overwrite.
  const redraftFromUserComment = async (userCommentEntry, opts = {}) => {
    if (!session) return;
    if (!userCommentEntry.source_entry_id) {
      toast.error("This comment isn't paired to a transcript slice — can't re-draft.");
      return;
    }
    const famId = userCommentEntry.source_entry_id;
    const famNudge = familyNudge[famId] || null;
    const famLength = familyLength[famId] || null;
    const nudgeId = opts.nudgeId || famNudge?.id;
    const length = opts.length ?? famLength;
    if (!nudgeId) {
      toast.error("Pick a Mold Silo Reaction first (mirrors across every card in this family).");
      return;
    }
    setBusyEntryId(userCommentEntry.id);
    try {
      const body = {
        source_entry_id: userCommentEntry.source_entry_id,
        nudge_command_id: nudgeId,
      };
      if (length) body.length = length;
      Object.assign(body, overrideAsAskBody("public_ask"));
      await runYtreAsyncJob(`/ytre/sessions/${session.id}/ask-silo`, body);
      const snap = await api.get(`/ytre/sessions/${session.id}`);
      setEntries(snap.data.entries || []);
      toast.success("Alternate silo comment drafted below.");
    } catch (e) {
      toast.error(friendlyPipelineError(e, "Re-draft failed."));
    } finally {
      setBusyEntryId(null);
    }
  };

  // Iter160y (Randy pt-ytre-load-flow-fix, Feb 2026) — save-first CTA
  // now serves BOTH URL paste AND saved-project tap. The three CTAs
  // (Keep editing / Discard & load / Save + load) branch on
  // `saveFirstPrompt.kind`. Modal chrome is identical between paths so
  // users see one consistent decision surface.
  // Feb 28 2026 (Randy pt-ytre-save-first-loading) — the modal's
  // Discard / Save actions kick off a ~15s loadUrl chain (session-
  // from-url + transcript fetch + comment ingest + auto-drafts).
  // Previously the modal closed instantly on click and the user
  // saw ~15s of black hole. Now we keep the modal open with a
  // spinner + copy until the action resolves.
  const [saveFirstBusy, setSaveFirstBusy] = useState(null); // "discard" | "save" | null
  const saveFirstCta = saveFirstPrompt ? (
    <div className="fixed inset-0 z-[240] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ytre-save-first-modal" onClick={() => { if (!saveFirstBusy) setSaveFirstPrompt(null); }}>
      <div className="w-full max-w-md bg-[#FDFCF8] border hairline rounded-xl shadow-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b hairline">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] font-medium">Save first?</div>
          <div className="text-[14px] font-medium text-[#1C1B1A]">You have unsaved work in the current project.</div>
        </div>
        <div className="p-4 text-[12px] text-[#4A5D4E]">
          {saveFirstBusy ? (
            <div className="flex items-center gap-2 text-[#4A5D4E]">
              <ArrowsClockwise size={13} weight="bold" className="animate-spin" />
              <span>{saveFirstBusy === "save" ? "Saving current project, then loading…" : "Loading fresh video…"} <span className="text-[#8A8C86]">(usually ~15s on production)</span></span>
            </div>
          ) : (saveFirstPrompt.kind === "project"
            ? "Loading this saved project will replace your current YTRE workspace. Save the current project to the bottom strip first, or discard it and open the saved one?"
            : "Loading a new video will replace your current YTRE workspace. Save the current project to the bottom strip first, or discard it and start fresh?")}
        </div>
        <div className="flex items-center justify-end gap-2 border-t hairline px-4 py-3 bg-white/40">
          <button onClick={() => setSaveFirstPrompt(null)} disabled={!!saveFirstBusy} className="text-[12px] px-3 py-1.5 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE] disabled:opacity-40" data-testid="ytre-save-first-cancel">Keep editing</button>
          <button
            onClick={async () => {
              setSaveFirstBusy("discard");
              try {
                if (saveFirstPrompt.kind === "project") {
                  await loadSavedProject(saveFirstPrompt.projectId, false);
                } else {
                  await loadUrl({ url: saveFirstPrompt.url, save: false });
                }
              } finally {
                setSaveFirstBusy(null);
                setSaveFirstPrompt(null);
              }
            }}
            disabled={!!saveFirstBusy}
            className="text-[12px] px-3 py-1.5 rounded-md text-[#C16E5A] border border-[#C16E5A]/50 hover:bg-[#F5E5E1] disabled:opacity-40 flex items-center gap-1.5"
            data-testid="ytre-save-first-discard"
          >
            {saveFirstBusy === "discard" && <ArrowsClockwise size={11} weight="bold" className="animate-spin" />}
            {saveFirstPrompt.kind === "project" ? "Discard & open" : "Discard & load new"}
          </button>
          <button
            onClick={async () => {
              const title = window.prompt("Name the project:", session?.video_title || "Untitled project");
              if (title == null) return; // cancel
              setSaveFirstBusy("save");
              try {
                if (saveFirstPrompt.kind === "project") {
                  await loadSavedProject(saveFirstPrompt.projectId, true);
                } else {
                  await loadUrl({ url: saveFirstPrompt.url, save: true, saveTitle: title });
                }
              } finally {
                setSaveFirstBusy(null);
                setSaveFirstPrompt(null);
              }
            }}
            disabled={!!saveFirstBusy}
            className="text-[12px] px-4 py-1.5 rounded-md bg-[#2F6B3A] text-white hover:bg-[#245029] flex items-center gap-1.5 disabled:opacity-40"
            data-testid="ytre-save-first-save"
          >
            {saveFirstBusy === "save" ? <ArrowsClockwise size={11} weight="bold" className="animate-spin" /> : <FloppyDisk size={11} weight="bold" />}
            {saveFirstPrompt.kind === "project" ? "Save + open" : "Save + load new"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="siloart-copy space-y-6" data-testid="ytre-tab">
      {/* Full-name header (Randy: tab says YTRE, header expands to full).
          Feb 2026 (Randy pt-siloart-ytre-fix) — border-b uses inline color
          instead of `.hairline` so this container isn't flagged as a
          "feature card" by the siloart-copy exclusion rule. The H2/paragraph
          below are LOOSE COPY sitting directly on the content canvas and
          must inherit the user's Silo Art V2 whitetext/blacktext setting. */}
      <div className="flex items-start justify-between gap-4 pb-2 border-b border-[#E5E5E0]">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#6B6A68] font-medium">YTRE</div>
          <h2 className="text-2xl font-serif text-[#1C1B1A] mt-0.5">YouTube Response Engine</h2>
          <p className="text-[12.5px] text-[#57534E] mt-1 max-w-xl">
            Co-watch a video with your silo. Capture transcript slices at any moment. Run Workbench operations on them. Invite your silo to respond with a chosen stance. Consolidate into one polished reply when you&apos;re ready.
          </p>
        </div>
        {session && (
          <div className="flex items-center gap-3 text-[11px] text-[#6B6A68]">
            <label className="flex items-center gap-1.5 cursor-pointer" data-testid="ytre-pin-toggle">
              <input type="checkbox" checked={!!session.pin_video} onChange={togglePinVideo} className="accent-[#4A5D4E]" />
              Pin video
            </label>
            {/* Feb 2026 (Randy pt-ytre-clear) — small, out-of-the-way clear
                button. Wipes the working-copy session + entries; saved
                projects stay intact. Confirms before firing. */}
            <button
              type="button"
              onClick={clearCanvas}
              data-testid="ytre-clear-canvas"
              title="Clear the YTRE canvas (wipes current working copy; saved projects are kept)"
              className="flex items-center gap-1 text-[10.5px] uppercase tracking-[0.14em] px-2 py-1 rounded-md border border-[#E5E5E0] text-[#C16E5A] hover:bg-[#F5E5E1] hover:border-[#C16E5A]/50 transition-colors"
            >
              <Trash size={11} weight="bold" />
              Clear
            </button>
          </div>
        )}
      </div>

      {/* URL input — always visible, primary entry point. */}
      <form onSubmit={onUrlSubmit} className="flex items-center gap-2" data-testid="ytre-url-form">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Paste a YouTube URL (watch, shorts, or youtu.be)…"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            className="w-full border border-[#D8D6D2] rounded-md p-2 pr-9 text-[13px] bg-white"
            data-testid="ytre-url-input"
          />
          {/* Iter171 Ask 4 — URL memory dropdown */}
          {urlHistory.length > 0 && (
            <button
              type="button"
              onClick={() => setUrlHistoryOpen((v) => !v)}
              data-testid="ytre-url-history-toggle"
              title={`Show last ${urlHistory.length} URL${urlHistory.length === 1 ? "" : "s"} you pasted here`}
              className="absolute top-1/2 -translate-y-1/2 right-2 w-6 h-6 flex items-center justify-center rounded hover:bg-[#F0F1EE] text-[#6B6A68]"
            >
              <CaretDown size={12} weight="bold" />
            </button>
          )}
          {urlHistoryOpen && urlHistory.length > 0 && (
            <div
              data-testid="ytre-url-history-popover"
              className="absolute z-40 right-0 top-full mt-1 w-full min-w-[320px] bg-white border hairline rounded-md shadow-lg overflow-hidden"
            >
              <div className="px-3 py-1.5 border-b hairline bg-[#F7FAF7] text-[10px] uppercase tracking-[0.16em] text-[#6B6A68]">
                Recent videos on this silo
              </div>
              {urlHistory.map((h) => {
                // Oct 2026 (Randy) — unread pip per row. Replies to threads
                // HIS hat is in, nothing else: a stranger's brand-new root
                // comment stays silent by his decision. The row already
                // carries the count, so this costs no extra request.
                const _unread = Number(h?.unread_activity?.new_replies || 0);
                const _hat = h?.unread_activity?.interaction_channel_name
                  || h?.interaction_channel_name || "";
                return (
                <div
                  key={h.id}
                  data-testid={`ytre-url-history-row-${h.video_id}`}
                  className="group relative w-full hover:bg-[#F0F1EE] border-b hairline last:border-b-0"
                >
                  <button
                    type="button"
                    onClick={async () => {
                      setUrlInput(h.url);
                      setUrlHistoryOpen(false);
                      // Trigger the load immediately with the picked URL
                      await loadUrl({ url: h.url });
                      // Opening the video IS reading it — same contract as
                      // the banner's "Load into YTRE" row.
                      if (_unread > 0) {
                        try {
                          await api.post(`/ytre/silo/${siloId}/activity/dismiss?video_id=${h.video_id}`);
                          const hh = await api.get(`/ytre/url-history?silo_id=${siloId}`);
                          setUrlHistory(hh.data?.history || []);
                          const aa = await api.get(`/ytre/silo/${siloId}/activity`);
                          setActivityAlerts(aa.data?.unread_urls || []);
                        } catch { /* silent */ }
                      }
                    }}
                    data-testid={`ytre-url-history-item-${h.video_id}`}
                    className="w-full text-left px-3 py-2 pr-16 text-[12px] text-[#1C1B1A] flex flex-col gap-0.5"
                    title={h.url}
                  >
                    <span className="truncate font-medium">{h.video_title || h.video_id}</span>
                    <span className="text-[10px] text-[#6B6A68] truncate">{h.url}</span>
                    {/* Feb 24, 2026 (Randy — wrong-channel banner) —
                        ambient hint of which channel is bound to this
                        silo. Lets the user spot a wrong-hat mismatch
                        before even loading the URL. */}
                    {h.connected_channel_title && (
                      <span
                        data-testid={`ytre-url-history-item-channel-${h.video_id}`}
                        className="text-[10px] text-[#4A5D4E] truncate italic"
                      >
                        Connected as: {h.connected_channel_title}
                      </span>
                    )}
                  </button>
                  {_unread > 0 && (
                    <span
                      data-testid={`ytre-url-history-dot-${h.video_id}`}
                      title={`${_unread} new ${_unread === 1 ? "reply" : "replies"}${_hat ? ` to ${_hat}` : ""} — open to read`}
                      className="absolute top-1/2 -translate-y-1/2 right-9 flex items-center gap-1 pointer-events-none"
                    >
                      <span className="text-[10px] font-semibold text-[#A03030] tabular-nums">{_unread}</span>
                      <span className="w-2 h-2 rounded-full bg-[#E04A3F] ring-2 ring-white shadow-sm" />
                    </span>
                  )}
                  {/* Feb 24, 2026 (Randy — dropdown delete) — one-tap
                      remove. Hover-reveal so the row isn't visually
                      noisy at rest. Stops propagation so the outer
                      row click (which loads) doesn't fire. */}
                  <button
                    type="button"
                    onClick={(e) => deleteHistoryRow(h.id, e)}
                    data-testid={`ytre-url-history-delete-${h.video_id}`}
                    aria-label="Remove from recent"
                    title="Remove from recent"
                    className="absolute top-1/2 -translate-y-1/2 right-2 w-6 h-6 flex items-center justify-center rounded text-[#6B6A68] hover:bg-[#F7E1E1] hover:text-[#A03030] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  >
                    <Trash size={12} weight="duotone" />
                  </button>
                </div>
                );
              })}
            </div>
          )}
        </div>
        <button type="submit" disabled={loadingUrl || !urlInput.trim()}
          data-testid="ytre-url-load"
          className="text-[12px] px-4 py-2 rounded-md bg-[#2F6B3A] text-white hover:bg-[#245029] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
          {loadingUrl
            ? <ArrowsClockwise size={13} weight="duotone" className="animate-spin" />
            : <PlayCircle size={13} weight="duotone" />}
          {loadingUrl ? "Loading…" : "Load"}
        </button>
      </form>

      {/* Iter171 Ask 1 — stage-hint under the URL bar while loading */}
      {loadingUrl && loadStageHint && (
        <div
          data-testid="ytre-url-load-hint"
          className="text-[11px] text-[#4A5D4E] italic px-1 -mt-1"
        >
          {loadStageHint}
        </div>
      )}

      {saveFirstCta}

      {/* Feb 24, 2026 (Randy — YTRE activity alerts) — top-of-workspace
          banner listing any recent videos where new replies landed
          since the last time we scanned. Each row is one-tap load-into-
          YTRE (auto-dismisses that URL) or bulk Dismiss-all. Renders
          nothing when there's no unread activity.

          Feb 28, 2026 (Randy pt-ytre-activity-hide-current) — hide
          the row for whichever video is CURRENTLY loaded, provided
          the session's ingest is at-least-as-recent as the alert's
          detection timestamp (i.e., the load already picked up the
          new replies). If the alert is fresher than the session,
          keep the row but swap "Load into YTRE" for "Refresh
          comments" so the user can pull the delta without a full
          session teardown. */}
      {(() => {
        const _visibleAlerts = activityAlerts.filter((row) => {
          if (!session || row.video_id !== session.video_id) return true;
          const sessTs = session.updated_at || session.created_at;
          const detTs = row?.unread_activity?.detected_at;
          if (!sessTs || !detTs) return false; // no signal → assume freshly loaded, hide
          return new Date(sessTs).getTime() < new Date(detTs).getTime();
        });
        return _visibleAlerts.length > 0 && (
        <div
          data-testid="ytre-activity-banner"
          className="border border-[#D4B3A1] bg-[#FFF3EB] rounded-lg overflow-hidden"
        >
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b hairline bg-[#F9E6D6]">
            <div className="flex items-center gap-2 text-[13px] font-medium text-[#7A3A1C]">
              <span
                className="w-2 h-2 rounded-full bg-[#E86A5C] ring-2 ring-white/70 shadow-sm"
                aria-hidden="true"
              />
              {_visibleAlerts.length === 1
                ? "1 of your recent videos has new activity"
                : `${_visibleAlerts.length} of your recent videos have new activity`}
            </div>
            <button
              type="button"
              data-testid="ytre-activity-banner-dismiss-all"
              onClick={async () => {
                try {
                  await api.post(`/ytre/silo/${siloId}/activity/dismiss`);
                  setActivityAlerts([]);
                } catch { /* silent */ }
              }}
              className="text-[11px] text-[#7A3A1C] hover:text-[#5A2A14] underline underline-offset-2 decoration-dotted"
            >
              Dismiss all
            </button>
          </div>
          <ul className="divide-y hairline">
            {_visibleAlerts.map((row) => {
              const meta = row.unread_activity || {};
              const isCurrent = session && row.video_id === session.video_id;
              return (
                <li key={row.video_id} className="px-4 py-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-medium text-[#1C1B1A] truncate">
                      {row.video_title || row.video_id}
                    </div>
                    {/* Sep 14 2026 (Randy: "I want all the alerts to show
                        the interacted channel") — whichever of his hats
                        actually holds this video's conversation. */}
                    {(row.interaction_channel_name || meta.interaction_channel_name || row.connected_channel_title) && (
                      <div
                        data-testid={`ytre-activity-row-channel-${row.video_id}`}
                        className="text-[10.5px] text-[#4A5D4E] mt-0.5 truncate"
                      >
                        Interaction by:{" "}
                        <strong className="font-semibold">
                          {row.interaction_channel_name || meta.interaction_channel_name || row.connected_channel_title}
                        </strong>
                        {_switchNeeded(row) && (
                          <span className="text-[#A05A2C]"> · not the connected channel</span>
                        )}
                      </div>
                    )}
                    <div className="text-[11px] text-[#6B4838] mt-0.5" data-testid={`ytre-activity-row-meta-${row.video_id}`}>
                      {(meta.new_replies || 0) > 0 ? (
                        <span>
                          {/* Sep 15 2026 (Randy: "2 new replies in 1 thread
                              — not thread[s] … each are in their own
                              thread") — the count used to be the length of
                              a list capped at 5 items, so it reported the
                              cap, not reality. `new_threads` carries the
                              true number of distinct threads holding
                              unread replies. */}
                          <strong>{meta.new_replies}</strong> new repl{meta.new_replies === 1 ? "y" : "ies"}{" "}
                          {(() => {
                            const n = meta.new_threads || (meta.per_thread_deltas || []).length || 1;
                            return n === 1 ? "in 1 thread" : `across ${n} threads`;
                          })()}
                        </span>
                      ) : (
                        <span>new activity on this video</span>
                      )}
                    </div>
                    {(meta.per_thread_deltas || [])[0]?.snippet && (
                      <div className="text-[10.5px] text-[#6B6A68] italic mt-0.5 line-clamp-1">
                        “{(meta.per_thread_deltas || [])[0].snippet}…”
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    data-testid={`ytre-activity-banner-load-${row.video_id}`}
                    onClick={async () => {
                      // Sep 14 2026 (Randy) — if the conversation lives on
                      // another of his channels, pop the switch first and
                      // load the moment the re-auth lands.
                      if (_switchNeeded(row)) {
                        setSwitchRow(row);
                        return;
                      }
                      // Optimistic strip from banner state, then load.
                      // Load itself will implicitly dismiss backend state
                      // via the auto-dismiss-on-load path (see below).
                      setActivityAlerts((rows) => rows.filter((r) => r.video_id !== row.video_id));
                      try {
                        await api.post(`/ytre/silo/${siloId}/activity/dismiss?video_id=${row.video_id}`);
                      } catch { /* silent */ }
                      loadUrl({ url: row.url });
                    }}
                    className="shrink-0 px-3 py-1.5 rounded-md bg-[#B54848] hover:bg-[#8f3838] text-white text-[11.5px] font-medium"
                  >
                    {isCurrent ? "Refresh comments" : _switchNeeded(row) ? "Switch & load" : "Load into YTRE"}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        );
      })()}

      {/* Feb 24, 2026 (Randy — wrong-channel banner). Fires when this
          video has comments but zero of them come from the currently
          connected channel. Actionable single-tap link opens the same
          Load Gate modal used to (dis)connect + reconnect a YouTube
          channel on this silo.

          Feb 26, 2026 (Randy pt-mismatch-banner-dismiss) — dismiss X
          for the session lets Randy hide the banner without touching
          any channel state.

          Feb 28, 2026 (Randy pt-mismatch-banner-persist-dismiss) —
          dismissal now remembered PER CHANNEL via localStorage.
          Randy: as long as the connected_channel_id doesn't change,
          the banner stays hidden across page reloads and session
          switches. Disconnecting + reconnecting a DIFFERENT channel
          naturally re-triggers the banner (different key). */}
      {quotaNotice && (
        <div
          data-testid="ytre-quota-banner"
          className="border border-[#E0A9A9] bg-[#FFF1F1] rounded-lg px-4 py-3 flex items-start gap-3"
        >
          <div className="flex-shrink-0 mt-0.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9B2C2C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="flex-1 text-[12.5px] text-[#7A2222] leading-snug">
            <div className="font-semibold">
              YouTube's daily allowance is used up — try again tomorrow.
            </div>
            <div className="mt-1 text-[#8C3A3A]">
              {quotaNotice.partial_replies
                ? "Some reply chains on this video are showing only their first few replies. Refresh after the reset to pull the rest."
                : "Comments for this video couldn't be read, so the canvas may be empty or missing threads. Nothing you've drafted has been lost."}
              {" "}The allowance resets at midnight Pacific.
            </div>
          </div>
        </div>
      )}
      {transcriptMissing && (
        <div
          data-testid="ytre-transcript-banner"
          className="border border-[#E0A9A9] bg-[#FFF1F1] rounded-lg px-4 py-3 flex items-start gap-3"
        >
          <div className="flex-shrink-0 mt-0.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9B2C2C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="flex-1 text-[12.5px] text-[#7A2222] leading-snug">
            <div className="font-semibold">
              No transcript loaded for this video — captures will come back empty.
            </div>
            <div className="mt-1 text-[#8C3A3A]">
              {session.transcript_fetch_error
                ? session.transcript_fetch_error.message
                : "This video was loaded before the reason was being recorded, so we don't know why yet."}
              {session.transcript_fetch_error?.reason === "ip_blocked"
                ? " This is the server's network, not your YouTube keys or this video — retrying often gets through."
                : " Tap Retry transcript to pull it now, or Run diagnostic to see exactly what this server gets from YouTube."}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={retryTranscript}
                disabled={retryingTranscript}
                data-testid="ytre-transcript-retry"
                className="px-3 py-1.5 rounded-md bg-[#9B2C2C] text-white text-[11.5px] font-medium hover:bg-[#7A2222] disabled:opacity-60 transition-colors"
              >
                {retryingTranscript ? "Retrying…" : "Retry transcript"}
              </button>
              <button
                type="button"
                onClick={runTranscriptProbe}
                data-testid="ytre-transcript-probe"
                className="px-3 py-1.5 rounded-md border border-[#C97C7C] text-[#7A2222] text-[11.5px] font-medium hover:bg-[#FBE3E3] transition-colors"
              >
                Run diagnostic
              </button>
            </div>
            {transcriptProbe && (
              <pre
                data-testid="ytre-transcript-probe-output"
                className="mt-2 text-[10.5px] leading-relaxed bg-white/70 border border-[#E0A9A9] rounded p-2 overflow-x-auto whitespace-pre-wrap text-[#5A1F1F]"
              >
                {transcriptProbe.loading
                  ? "Running both transcript paths…"
                  : transcriptProbe.error
                    ? transcriptProbe.error
                    : [
                      `outbound IP : ${transcriptProbe.outbound_ip}`,
                      `proxy       : ${transcriptProbe.proxy?.enabled ? `ON (${transcriptProbe.proxy.mode}) · ${transcriptProbe.proxy.endpoint} · exit IP ${transcriptProbe.proxy.exit_ip}` : "OFF — requests leave from this server's own IP"}`,
                      `libraries   : ${JSON.stringify(transcriptProbe.libraries)}`,
                      `primary     : ${transcriptProbe.primary?.ok ? "OK" : "FAILED"} · ${transcriptProbe.primary?.entry_count || 0} lines · ${transcriptProbe.primary?.elapsed_ms}ms${transcriptProbe.primary?.error ? `\n              ${String(transcriptProbe.primary.error).replace(/\s+/g, " ").slice(0, 300)}` : ""}`,
                      `fallback    : ${transcriptProbe.fallback?.ok ? "OK" : "FAILED"} · ${transcriptProbe.fallback?.entry_count || 0} lines · ${transcriptProbe.fallback?.elapsed_ms}ms${transcriptProbe.fallback?.error ? `\n              ${String(transcriptProbe.fallback.error).replace(/\s+/g, " ").slice(0, 300)}` : ""}`,
                      `verdict     : ${transcriptProbe.verdict}`,
                    ].join("\n")}
              </pre>
            )}
          </div>
        </div>
      )}
      {session && channelMismatch
          && channelMismatch.integration_present
          && _wrongHat(channelMismatch)
          && !mismatchBannerDismissed
          && (typeof window === "undefined" || !window.localStorage.getItem(_mismatchKey(channelMismatch, session))) && (
        <div
          data-testid="ytre-channel-mismatch-banner"
          className="border border-[#E4C878] bg-[#FFF8E1] rounded-lg px-4 py-3 flex items-start gap-3 relative"
        >
          <div className="flex-shrink-0 mt-0.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8A6A00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div className="flex-1 text-[12.5px] text-[#5C4A00] leading-snug pr-6">
            {channelMismatch.interaction_channel_id ? (
              <>
                {/* Sep 16 2026 — NOTE for whoever sees this look broken in
                    PREVIEW: Emergent's visual editor wraps every JSX
                    expression in a <span data-ve-dynamic x-dynamic
                    style="display:contents"> that computes to
                    color:rgb(255,255,255), so interpolated values render
                    white-on-cream here. It is a preview-only artifact —
                    verified absent from the production bundle
                    (main.a332e7b0.js on my-silo.com) — and it cannot be
                    overridden from inside the component, because the
                    instrumentation re-wraps any span you add. Do not add
                    defensive markup for it; the styling below is correct. */}
                <div className="font-semibold">
                  This conversation belongs to{" "}
                  {channelMismatch.interaction_channel_name
                    ? `"${channelMismatch.interaction_channel_name}"`
                    : "another of your channels"}
                  {channelMismatch.connected_channel_title
                    ? ` — but this silo is connected as "${channelMismatch.connected_channel_title}".`
                    : "."}
                </div>
                <div className="mt-1 text-[#6B5300]">
                  Replies you post from here would come from the wrong channel.
                </div>
              </>
            ) : (
              <>
                <div className="font-semibold">
                  {channelMismatch.total_threads} comment{channelMismatch.total_threads === 1 ? "" : "s"} on this video — none from any channel you&apos;ve connected{channelMismatch.connected_channel_title ? `, including "${channelMismatch.connected_channel_title}"` : ""}.
                </div>
                <div className="mt-1 text-[#6B5300]">
                  If you commented from a YouTube channel we haven&apos;t seen yet, connect it once and we&apos;ll remember it.
                </div>
              </>
            )}
            {/* Sep 16 2026 (Randy: "if you recognize that I was 'hunting'
                for this. Then yes.") — the primary action is now the switch
                he was actually hunting for. Disconnect is demoted to the
                small secondary link below, because disconnecting was never
                what he wanted; it was just the only door that opened. */}
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <YtreChannelPicker
                siloId={siloId}
                connectedChannelId={channelMismatch.connected_channel_id}
                onSwitched={() => refreshEntries()}
                onConnectNew={() => setLoadGateOpen(true)}
                label={channelMismatch.interaction_channel_name
                  ? `Switch to ${channelMismatch.interaction_channel_name} →`
                  : "Switch channel →"}
                testid="ytre-channel-mismatch-switch"
              />
              <button
                type="button"
                onClick={() => setLoadGateOpen(true)}
                data-testid="ytre-channel-mismatch-reconnect"
                className="text-[11px] text-[#6B5300]/80 hover:text-[#5C4A00] underline decoration-dotted underline-offset-2"
              >
                Disconnect &amp; reconnect instead
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setMismatchBannerDismissed(true);
              try {
                const k = _mismatchKey(channelMismatch, session);
                window.localStorage.setItem(k, "1");
              } catch { /* private mode / quota — session-scoped fallback still works */ }
            }}
            data-testid="ytre-channel-mismatch-dismiss"
            title="Hide this banner. Stays hidden until you disconnect and reconnect a different channel."
            className="absolute top-2 right-2 p-1 rounded hover:bg-[#F5E7B8] text-[#8A6A00]"
          >
            <X size={12} weight="bold" />
          </button>
        </div>
      )}

      {/* Empty state — no working copy yet. */}
      {!session && !loadingUrl && (
        <div className="border border-dashed border-[#D8D6D2] rounded-lg p-10 text-center bg-white/90" data-testid="ytre-empty">
          <PlayCircle size={40} weight="duotone" className="text-[#6B6A68]/50 mx-auto" />
          <div className="text-[13px] font-medium text-[#1C1B1A] mt-3">No YTRE project loaded yet.</div>
          <div className="text-[11.5px] text-[#6B6A68] mt-1">Paste a YouTube URL above to start co-watching with your silo.</div>
        </div>
      )}

      {/* Active project. */}
      {session && (
        <>
          <VideoPlayerSection session={session} playerRef={playerRef} currentTime={currentTime} />

          {/* Capture panel. */}
          <div className="bg-white border hairline rounded-lg p-4" data-testid="ytre-capture-panel">
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={capture}
                disabled={capturing}
                data-testid="ytre-capture-btn"
                className="text-[13px] px-5 py-2.5 rounded-md bg-[#1C1B1A] text-white hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 font-medium">
                <Lightning size={14} weight="fill" />
                {capturing ? "Capturing…" : "Capture point"}
              </button>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-[#4A5D4E]" data-testid="ytre-since-last-toggle">
                  <input type="checkbox" checked={useSinceLast} onChange={(e) => setUseSinceLast(e.target.checked)} className="accent-[#2F6B3A]" />
                  Since last capture
                </label>
                <div className={`flex items-center gap-2 ${useSinceLast ? "opacity-40 pointer-events-none" : ""}`}>
                  <span className="text-[10.5px] uppercase tracking-[0.14em] text-[#6B6A68]">Prior:</span>
                  <input
                    type="range"
                    min={MPRIOR_MIN_IDX}
                    max={MPRIOR_MAX_IDX}
                    step={1}
                    value={mpriorIdx >= 0 ? mpriorIdx : 4}
                    onChange={(e) => setMinutesPrior(MPRIOR_PRESETS[Number(e.target.value)])}
                    data-testid="ytre-mprior-slider"
                    className="w-40 accent-[#2F6B3A]"
                    aria-label="Capture window prior to current playhead"
                  />
                  <span className="text-[12px] font-mono w-10 tabular-nums text-right" data-testid="ytre-mprior-value">{MPRIOR_LABELS[mpriorIdx >= 0 ? mpriorIdx : 4]}</span>
                </div>
              </div>
              <div className="text-[11px] text-[#6B6A68] ml-auto" data-testid="ytre-playhead-hint">
                Playhead: <span className="font-mono">{fmtTime(currentTime)}</span> · Last capture end: <span className="font-mono" data-testid="ytre-last-capture-end">{fmtTime(liveLastCaptureEnd)}</span>
              </div>
            </div>
          </div>

          {/* Iter172 (Randy pt-ytre-refresh-and-minimize, Feb 13, 2026)
              — thin button strip between the capture panel and the
              workspace. LEFT: light refresh (re-fetch entries only,
              no LLM). RIGHT: minimize-old (collapse every card except
              the last-interacted one). Only visible when a session
              has at least one entry. */}
          {entries.length > 0 && (
            <div className="flex items-center justify-between px-1" data-testid="ytre-workspace-toolbar">
              <button
                type="button"
                onClick={refreshEntries}
                disabled={refreshingHistory || autoSyncing}
                data-testid="ytre-workspace-refresh"
                title="Refresh reply history — re-fetches entries from the server without a full URL reload."
                className="text-[10.5px] px-2 py-1 rounded border hairline text-[#4A5D4E] bg-white hover:bg-[#F4F3EE] disabled:opacity-50 flex items-center gap-1"
              >
                <ArrowsClockwise size={9} weight="duotone" className={(refreshingHistory || autoSyncing) ? "animate-spin" : ""} />
                {autoSyncing ? "Syncing…" : refreshingHistory ? "Refreshing…" : "Refresh"}
              </button>
              {/* Iter172 audit-7 (Randy pt-ytre-canvas-search, Feb 17, 2026)
                  — inline search for the current session's canvas. */}
              <YtreCanvasSearch
                query={searchQuery}
                onQueryChange={setSearchQuery}
                totalMatches={searchMatches.length}
                activeIdx={searchMatches.length ? activeMatchIdx + 1 : 0}
                onPrev={onSearchPrev}
                onNext={onSearchNext}
                wrapPulseTick={searchWrapPulseTick}
              />
              <button
                type="button"
                onClick={runMinimizeAll}
                data-testid="ytre-workspace-minimize-all"
                title={allMinimized
                  ? "Open every card back to its preview view. Cards can still be collapsed or fully expanded individually via their chevron."
                  : "Minimize every card to header-only. Tap again to open them all back to preview."}
                className="text-[10.5px] px-2 py-1 rounded border hairline text-[#4A5D4E] bg-white hover:bg-[#F4F3EE] flex items-center gap-1"
              >
                {allMinimized
                  ? <CaretUp size={9} weight="duotone" />
                  : <CaretDown size={9} weight="duotone" />}
                {allMinimized ? "Open all" : "Minimize all"}
              </button>
            </div>
          )}



          {/* Workspace — chronological cards, grouped into transcript
              families. Iter160s (Randy pt-ytre-family-tether, Feb 2026)
              — Randy audit: the transcript, its silo_comment children
              and any existing user_comment children are conceptually ONE
              thread. Rendering them with `space-y-3` between every
              entry made them look like independent pills. Fix: group
              each transcript with its children into a family wrapper,
              KEEP the `space-y-3` between families (visual breathing
              room across the workspace) but drop the gap INSIDE a
              family (transcript butts right up against its children)
              AND indent the children ~28px so the parent-child
              relationship reads at a glance. */}
          <div className="space-y-3" data-testid="ytre-workspace" style={{ overflowAnchor: "auto" }}>
            {entries.length === 0 && (
              <div className="text-[12px] text-[#6B6A68] text-center py-8 italic">
                Watch the video, then tap <strong>Capture point</strong> at moments you want to think about.
              </div>
            )}
            {(() => {
              // Group entries into families. A family = a transcript +
              // every silo_comment / user_comment whose source_entry_id
              // points to it. Standalone user_comments (no source
              // transcript) and any orphan responses render as their
              // own single-card family so nothing gets dropped from
              // the canvas.
              const families = [];
              const consumedIds = new Set();
              for (const entry of entries) {
                if (consumedIds.has(entry.id)) continue;
                if (entry.kind === "transcript") {
                  const children = entries.filter((e2) =>
                    (e2.kind === "silo_response" || e2.kind === "user_comment")
                    && e2.source_entry_id === entry.id
                  );
                  children.forEach((c) => consumedIds.add(c.id));
                  families.push({ transcript: entry, children });
                  consumedIds.add(entry.id);
                } else {
                  // orphan child (source transcript deleted) or a
                  // standalone user_comment. Render as its own family.
                  families.push({ transcript: null, children: [entry] });
                  consumedIds.add(entry.id);
                }
              }

              const renderEntry = (entry, extraProps = {}) => {
                const familyId = entry.kind === "transcript"
                  ? entry.id
                  : (entry.source_entry_id || entry.id);
                const hasResponseBelow = entry.kind === "transcript"
                  ? entries.some((e2) => (
                      (e2.kind === "silo_response" || e2.kind === "user_comment")
                      && e2.source_entry_id === entry.id
                    ))
                  : false;
                return (
                  <EntryCard
                    key={entry.id}
                    entry={entry}
                    sessionId={session?.id}
                    siloId={siloId}
                    nudges={nudges}
                    commands={commands}
                    busy={busyEntryId === entry.id}
                    redraftArmed={!!redraftArmed[entry.id]}
                    onOp={(commandId, commandName) => runOpOnEntry(entry.id, commandId, commandName)}
                    onAskSilo={(nudgeCommandId, nudgeLabel, length, moldingHint) => askSilo(entry.id, nudgeCommandId, nudgeLabel, length, moldingHint)}
                    onSaveEdit={(patch) => saveEntryEdit(entry.id, patch)}
                    onDelete={() => deleteEntry(entry.id)}
                    onOpenRedraftPad={() => openRedraftPad(entry.id)}
                    onRedraftSiloResponse={(length, cardNudge, cardTone) => redraftSiloResponse(entry, length, cardNudge, cardTone)}
                    onRedraftFromUserComment={(userCommentEntry, opts) => redraftFromUserComment(userCommentEntry, opts)}
                    onApprove={(ownerEntry) => approveAndPost(ownerEntry.id)}
                    onIgnore={(ownerEntry, ignored) => ignoreEntry(ownerEntry.id, ignored)}
                    onPostSiloResponse={(siloEntry, opts) => postSiloResponse(siloEntry.id, siloEntry.silo_response_text, opts)}
                    onRedraft={(ownerEntry, payload) => redraftOwnerCard(ownerEntry.id, payload)}
                    onCreateCustomNudge={() => { setCustomNudgeEdit(null); setCustomNudgeOpen(true); }}
                    onCreateCustomOp={() => setOpsModalOpen(true)}
                    onDeleteCustomNudge={deleteCustomNudge}
                    onEditCustomNudge={editCustomNudge}
                    onOpenLlmPicker={() => setLlmPickerOpen(true)}
                    activeLlmOverride={activeOverride}
                    onRecapture={recaptureEntry}
                    hasResponseBelow={hasResponseBelow}
                    siloSlug={siloSlug}
                    familyId={familyId}
                    familyNudge={familyNudge[familyId] || null}
                    familyLength={familyLength[familyId] || null}
                    onStageFamilyNudge={(nudge) => stageFamilyNudge(familyId, nudge)}
                    onStageFamilyLength={(presetId) => {
                      // Iter171 Ask 5 → iter172 (Randy pt-ytre-per-card-
                      // settings, Feb 13, 2026) — Length is now
                      // per-card, no cascade, no silo-default persist.
                      // The transcript's Length picker just stages its
                      // OWN length locally (via familyLength map).
                      // Randy tunes the auto-response default via the
                      // new "Set auto response Length" thin card at
                      // the bottom of the YTRE tab.
                      stageFamilyLength(familyId, presetId);
                    }}
                    minimizeSignal={minimizeTick}
                    minimizeStage={minimizeStage}
                    searchQuery={searchQuery}
                    searchInfo={perEntrySearchInfo[entry.id] || null}
                    revealTargetId={revealTargetId}
                    revealNonce={revealNonce}
                    activeGuidance={guidanceByEntry[entry.id] || null}
                    onEditGuidance={() => openRedraftPad(entry.id)}
                    onClearGuidance={() => clearGuidance(entry.id)}
                    {...extraProps}
                  />
                );
              };

              return families.map((fam, famIdx) => {
                const key = fam.transcript ? fam.transcript.id : (fam.children[0]?.id || famIdx);
                // Feb 16 2026 (Randy pt-ytre-recapture-persist) —
                // family timestamp = parent transcript's CURRENT
                // time_end_sec. Passed to every child so a Recapture
                // on the parent instantly relabels every child card's
                // "@1:12" chip.
                const familyTs = (fam.transcript && typeof fam.transcript.time_end_sec === "number")
                  ? fam.transcript.time_end_sec
                  : null;
                return (
                  <div key={key} data-testid={`ytre-family-${key}`}>
                    {fam.transcript && renderEntry(fam.transcript)}
                    {fam.children.length > 0 && (
                      <div className="ml-7" data-testid={`ytre-family-children-${key}`}>
                        {fam.children.map((c) => renderEntry(c, { familyTimestamp: familyTs }))}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>

          {/* Project actions — save / discard / master reply. */}
          <div className="flex items-center gap-2 pt-4 border-t hairline flex-wrap">
            <button onClick={openMasterReply}
              data-testid="ytre-open-master-reply"
              className="text-[12px] px-4 py-2 rounded-md bg-[#2F6B3A] text-white hover:bg-[#245029] disabled:opacity-40 flex items-center gap-1.5 font-medium"
              disabled={entries.length === 0}>
              <MagicWand size={13} weight="duotone" /> Compile master reply
            </button>
            <button onClick={saveCurrentProject} className="text-[11.5px] px-3 py-1.5 rounded-md bg-white border border-[#E4E6E1] hover:bg-[#F0F1EE]/60 flex items-center gap-1.5" data-testid="ytre-save-current">
              <FloppyDisk size={12} weight="duotone" /> Save current project
            </button>
            <button onClick={discardCurrentProject} className="silo-loose text-[11.5px] px-3 py-1.5 rounded-md text-[#C16E5A] hover:bg-[#F5E5E1] flex items-center gap-1.5" data-testid="ytre-discard-current">
              <Trash size={12} weight="duotone" /> Discard
            </button>
          </div>

          {/* Two-sided source export bar — Iter160c. Compiles the full raw
              workspace (transcript slices + silo responses in chronological
              order) and offers the same trio of destinations the master
              reply does: Copy · Send to Silo (DocSaveDialog) · Send to
              Workbench. Separate from the master reply so users can export
              their thinking-trail even if they never compile the final
              polished YouTube comment. */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="ytre-two-sided-bar">
            <span className="text-[10.5px] uppercase tracking-[0.16em] text-[#6B6A68] mr-1">Two-sided source:</span>
            <button onClick={copyTwoSided} data-testid="ytre-two-sided-copy"
              className="text-[11.5px] px-3 py-1.5 rounded-md border hairline bg-white text-[#4A5D4E] hover:bg-[#F0F1EE]/60 flex items-center gap-1.5">
              <CopyIcon size={11} weight="duotone" /> Copy
            </button>
            <button onClick={() => setTwoSidedSiloDialogOpen(true)} data-testid="ytre-two-sided-send-silo"
              className="text-[11.5px] px-3 py-1.5 rounded-md border hairline bg-white text-[#4A5D4E] hover:bg-[#F0F1EE]/60 flex items-center gap-1.5">
              <PaperPlaneRight size={11} weight="duotone" /> Send to Silo
            </button>
            <button onClick={sendTwoSidedToWorkbench} data-testid="ytre-two-sided-send-workbench"
              className="text-[11.5px] px-3 py-1.5 rounded-md border hairline bg-white text-[#4A5D4E] hover:bg-[#F0F1EE]/60 flex items-center gap-1.5">
              <ShareNetwork size={11} weight="duotone" /> Send to Workbench
            </button>
          </div>
        </>
      )}

      {/* YouTube channel connection — slim YTRE-branded status card.
          Shares the (silo, channel1) OAuth row with Workbench YT Replies
          so filling one auto-populates the other; UI is intentionally
          minimal (no ad copy / sample chat / SKU pill / Manage). */}
      <div data-testid="ytre-oauth-crosslink" className="pt-2">
        <YtreYouTubeConnectCard siloId={siloId} />
      </div>

      {/* Iter172 audit-6 (Randy pt-ytre-no-autodraft, Feb 13, 2026) —
          "Set auto response Length" thin card removed. URL load no
          longer generates LLM autodrafts (see /app/memory/
          YTRE_NO_AUTODRAFT_SPEC.md), so the setting it controlled is
          dead. Any per-card length picks continue to work as local
          per-card state via the Length pill on each card. */}

      {/* Saved projects strip — always visible at the bottom. */}
      {savedProjects.length > 0 && (
        <div className="pt-6 border-t hairline" data-testid="ytre-saved-strip">
          <div className="silo-loose text-[10.5px] uppercase tracking-[0.16em] text-[#6B6A68] mb-2">Saved projects</div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {savedProjects.map((p) => (
              // Iter160h (Randy pt-ytre-p2 followup) — delete corner on
              // every saved-project tile. Card body still loads on click;
              // trash icon in the top-right is its own button and
              // stopPropagation prevents the load from firing. Confirm
              // dialog first so a stray tap can't nuke a project.
              <div key={p.id} className="relative shrink-0 w-56 group">
                <button onClick={() => loadSavedProject(p.id)}
                  data-testid={`ytre-saved-project-${p.id}`}
                  className="w-full border border-[#E4E6E1] rounded-md p-3 bg-white hover:bg-[#F7F8F5] text-left">
                  <div className="text-[11.5px] font-medium text-[#1C1B1A] truncate pr-6">{p.project_title || p.video_title || "Untitled"}</div>
                  <div className="text-[10.5px] text-[#6B6A68] mt-0.5 truncate">{p.video_id}</div>
                  <div className="text-[10px] text-[#6B6A68] mt-1">{p.saved_at ? new Date(p.saved_at).toLocaleDateString() : ""}</div>
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); deleteSavedProject(p); }}
                  data-testid={`ytre-saved-project-delete-${p.id}`}
                  className="absolute top-1.5 right-1.5 p-1 rounded text-[#C16E5A] hover:bg-[#F5E5E1] opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete this saved project"
                >
                  <Trash size={12} weight="duotone" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shared Re-draft capture pad — same drawer YT Replies opens, so
          silo memory can be updated before regenerating an op or a silo
          response. On close, `redraftArmed[entryId]` flips true so the
          next tap on the entry's Re-draft button actually re-runs the
          LLM. Iter160b. */}
      {redraftPad && (
        <YTRedraftCapturePad
          siloId={session?.silo_id || siloId}
          initialText={guidanceByEntry[redraftPad.entryId]?.text || ""}
          initialPersist={!!guidanceByEntry[redraftPad.entryId]?.persist}
          onClose={(didPush, guidance, persist) => closeRedraftPad(didPush, guidance, persist)}
        />
      )}

      {/* Master Reply modal — compiles the whole session into one
          publish-ready comment. Reuses YT Replies' patterns: staging
          Length dropdown, Re-draft with capture-pad pre-open, Send to
          Silo via DocSaveDialog, Post to YouTube via /post. Iter160b. */}
      {masterOpen && session && (
        <MasterReplyModal
          session={session}
          siloId={siloId}
          siloName={siloName}
          siloSlug={siloSlug}
          allSilos={allSilos}
          onClose={() => setMasterOpen(false)}
          onPatchSession={(patch) => setSession((s) => (s ? { ...s, ...patch } : s))}
        />
      )}

      {/* Two-sided source → Send to Silo. Shared DocSaveDialog — same
          silo picker + tier picker + editable title as every other
          push-to-silo on the platform. */}
      {twoSidedSiloDialogOpen && session && (
        <DocSaveDialog
          open={twoSidedSiloDialogOpen}
          onClose={() => setTwoSidedSiloDialogOpen(false)}
          content={twoSidedContent || "…"}
          sourceSiloId={siloId}
          silos={(allSilos && allSilos.length ? allSilos : [{ id: siloId, name: siloName || "this silo" }]).map((s) => ({ id: s.id, name: s.name }))}
          defaultTitle={`YTRE two-sided — ${session.video_title || session.video_id}`}
          initialTier={3}
          dialogTitle="Push two-sided source to silo"
          onSaved={() => { toast.success("Two-sided source saved into silo."); setTwoSidedSiloDialogOpen(false); }}
        />
      )}

      {/* Custom nudge builder — user-scoped equivalent of the SuperAdmin
          [YTRE — Global Commands] editor. AI-Buddy assists the user in
          crafting a nudge instruction. POSTs /commands with scope=ytre. */}
      {customNudgeOpen && (
        <YtreCustomNudgeModal
          key={customNudgeEdit?.id || "new"}
          siloId={siloId}
          editing={customNudgeEdit}
          onClose={() => { setCustomNudgeOpen(false); setCustomNudgeEdit(null); }}
          onCreated={onCustomNudgeCreated}
        />
      )}

      {/* Operations live in one pool (scope=slots) with the Workbench, so the
          "Create custom operation" row in Mold transcript opens that very
          dialog here — Randy: "if you make a new operation here, or there its
          the same". */}
      {opsModalOpen && (
        <CustomCommandsModal
          siloId={siloId}
          scope="slots"
          commands={commands}
          onReload={reloadOps}
          onClose={() => setOpsModalOpen(false)}
          zClass="z-[240]"
        />
      )}

      {/* Iter160e (Randy) — Smart LOAD-gate CTA. Fires when the user
          tries to load a URL but has no YouTube connected on this silo.
          Offers a one-tap mirror from any of their existing OAuth rows
          elsewhere, or a direct "Connect fresh" jump into the YTRE
          setup modal. */}
      {switchRow && (
        <YtreChannelSwitchModal
          siloId={siloId}
          row={switchRow}
          connectedName={boundChannel?.youtube_channel_name}
          onClose={() => setSwitchRow(null)}
          onSwitched={async () => {
            const row = switchRow;
            setSwitchRow(null);
            try { setBoundChannel(await ytApi.getIntegration(siloId, "channel1")); } catch { /* keep old */ }
            setActivityAlerts((rows) => rows.filter((r) => r.video_id !== row.video_id));
            try {
              await api.post(`/ytre/silo/${siloId}/activity/dismiss?video_id=${row.video_id}`);
            } catch { /* silent */ }
            loadUrl({ url: row.url });
          }}
        />
      )}

      {loadGateOpen && (
        <YtreLoadGateModal
          siloId={siloId}
          alternatives={loadGateAlternatives}
          onClose={() => setLoadGateOpen(false)}
          onConnected={() => { setLoadGateOpen(false); toast.info("Credentials mirrored. Finish the Google sign-in in the new tab, then come back and tap Load."); }}
        />
      )}

      {/* Sep 10 2026 (Randy) — delete confirmation. Deletes are permanent
          now (they tombstone the saved capture window / thread so a reload
          can't resurrect the card), so the warning has to be reliable. The
          native window.confirm this replaces was being silently suppressed
          by Chrome after a few dialogs. */}
      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="ytre-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDelete?.childCount > 0
                ? `Delete this card and ${pendingDelete.childCount} card${pendingDelete.childCount === 1 ? "" : "s"} under it?`
                : "Delete this card?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block font-medium text-[#3A3A38]">{pendingDelete?.label}</span>
              {pendingDelete?.childCount > 0 && (
                <span className="block">
                  The comments and replies indented under it go too.
                </span>
              )}
              <span className="block">
                <strong>This is permanent.</strong> {pendingDelete?.isCapture
                  ? "The saved capture window is removed, so this card will not come back the next time you load this video or hit Refresh."
                  : "It will not come back the next time you load this video or hit Refresh."}
              </span>
              <span className="block text-[12px] text-[#6B6A68]">
                Your YouTube comments are untouched — this only clears the card from this canvas.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ytre-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-[#C16E5A] hover:bg-[#A85742] text-white"
              data-testid="ytre-delete-confirm-btn"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter160e (Randy) — Session-only LLM surface picker for the
          public_ask surface (which YTRE's /ask-silo loopback shares
          with Public Q&A). Picking a model here echoes on every
          /ask-silo call this tab-session until the tab is closed
          or refreshed. Never persisted to the silo. */}
      {llmPickerOpen && (
        <SessionLlmPickerModal
          surface="public_ask"
          siloId={siloId}
          onClose={() => setLlmPickerOpen(false)}
          onSaved={() => setLlmOverrideTick((n) => n + 1)}
        />
      )}

      {/* Sep 29, 2026 (Randy pt-voice-kit) — "flag as 100% original response
          for voice cadence training", only after a substantial hand-edit. */}
      <VoiceOriginalPrompt
        open={!!voiceAsk}
        ratio={voiceAsk?.ratio || 0}
        onDecide={decideVoiceOriginal}
        text={voiceAsk?.text || ""}
        siloId={siloId}
        siloName={siloName}
        silos={allSilos && allSilos.length ? allSilos.map((s) => ({ id: s.id, name: s.name })) : null}
        defaultTitle={`My words — ${session?.video_title || "YouTube transcript"}`}
      />
    </div>
  );
}


// ─────────────────────────────────────────────────────────
// Video player — YouTube iframe with native controls intact.
// ─────────────────────────────────────────────────────────
function VideoPlayerSection({ session, playerRef, currentTime }) {
  // enablejsapi=1 lets us postMessage getCurrentTime / listen to onReady.
  // NOTE: we intentionally do NOT set `&origin=...` — some preview
  // environments (including ours) proxy the page through a wildcard
  // subdomain that YouTube's origin-check refuses, causing a "Video
  // unavailable" error. The postMessage bridge works fine without it,
  // as long as we register listening with the `widget` channel key.
  //
  // Feb 28 2026 (Randy pt-ytre-mobile-player-watchdog) — mobile
  // Safari's YT embed occasionally fails silently (black box, no
  // handshake, no error). Randy: "sometimes I'm just getting black
  // box. it's loading the comments but the video is not loading up."
  // Watchdog listens for the player-ready postMessage; if it hasn't
  // fired within 6s of iframe mount, we show a Reload button that
  // busts the src with a cachebuster and rekeys the iframe (forces
  // a fresh mount + handshake).
  const [ready, setReady] = useState(false);
  const [showReload, setShowReload] = useState(false);
  const [nonce, setNonce] = useState(0);
  const baseSrc = `https://www.youtube.com/embed/${session.video_id}?enablejsapi=1&rel=0`;
  const src = nonce > 0 ? `${baseSrc}&_r=${nonce}` : baseSrc;
  useEffect(() => {
    setReady(false);
    setShowReload(false);
    const onMsg = (ev) => {
      // Feb 28 v5 (Randy pt-ytre-watchdog-strict-listener): the previous
      // loose filter flipped ready=true on ANY youtube-origin message,
      // which YouTube's embed fires ambiently (infoDelivery every ~500ms
      // for player state / current time). Watchdog therefore never got
      // a chance to trip. Mirror the parent effect at line 445: parse
      // the payload and require an actual handshake event.
      if (typeof ev?.origin === "string" && !ev.origin.includes("youtube")) return;
      let data = ev.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch { return; }
      }
      if (!data || typeof data !== "object") return;
      if (data.event === "onReady" || data.event === "initialDelivery") {
        setReady(true);
      }
    };
    window.addEventListener("message", onMsg);
    const watchdog = setTimeout(() => {
      // Only trip if still not ready. If ready fired, we've already
      // cleared the timer via the message handler's setReady.
      setShowReload((cur) => cur || true);
    }, 6000);
    return () => {
      window.removeEventListener("message", onMsg);
      clearTimeout(watchdog);
    };
  }, [session.video_id, nonce]);
  useEffect(() => {
    if (ready) setShowReload(false);
  }, [ready]);
  const pinClass = session.pin_video ? "sticky top-4 z-10" : "";
  return (
    <div className={`bg-black/5 border hairline rounded-lg p-3 ${pinClass}`} data-testid="ytre-video-section">
      <div className="aspect-video bg-black rounded-md overflow-hidden relative">
        <iframe
          key={`player-${session.video_id}-${nonce}`}
          id={`ytre-player-${session.id}`}
          ref={playerRef}
          src={src}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
          title="YTRE video player"
          data-testid="ytre-video-iframe"
        />
        {showReload && !ready && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
            <div className="text-center px-4">
              <div className="text-[12.5px] text-white/90 mb-2 font-medium">Video player didn&apos;t respond</div>
              <div className="text-[11px] text-white/60 mb-3 max-w-[240px] mx-auto">Mobile Safari occasionally drops the YouTube handshake. Tap to force a fresh load.</div>
              <button
                type="button"
                onClick={() => { setShowReload(false); setNonce((n) => n + 1); }}
                data-testid="ytre-video-reload"
                className="text-[12px] px-4 py-1.5 rounded-md bg-[#B54848] text-white hover:bg-[#8f3838] font-medium"
              >
                Reload player
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="mt-2 text-[11px] text-[#6B6A68] flex items-center gap-3 flex-wrap">
        <span data-testid="ytre-video-id">Video: <span className="font-mono">{session.video_id}</span></span>
        <span className="ml-auto">Playing at: <span className="font-mono">{fmtTime(currentTime)}</span></span>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────
// Entry card — dispatches by kind (transcript / silo_response / note).
// ─────────────────────────────────────────────────────────
// Sep 30, 2026 (Randy pt-ytre-draft-autosave) — shown when a card reopens with
// an edit that was never saved. Names the age so he can tell "the thing I was
// mid-way through" from a keystroke he abandoned last week.
function DraftRecoveredBanner({ entryId, at, onRevert }) {
  return (
    <div
      className="mb-2 flex items-center gap-2 text-[11px] text-[#8A6A3A] bg-[#FBF6EC] border border-[#E6D9BE] rounded px-2 py-1.5"
      data-testid={`ytre-draft-recovered-${entryId}`}
    >
      <FloppyDisk size={12} weight="duotone" />
      <span className="flex-1">
        Unsaved edit restored — you typed this {draftAgeLabel(at)} and never hit Save.
      </span>
      <button
        type="button"
        onClick={onRevert}
        data-testid={`ytre-draft-revert-${entryId}`}
        className="underline hover:no-underline"
      >
        Throw it away
      </button>
    </div>
  );
}

function EntryCard({ entry, sessionId, siloId, nudges, commands, busy, redraftArmed, onOp, onAskSilo, onSaveEdit, onDelete, onOpenRedraftPad, onRedraftSiloResponse, onRedraftFromUserComment, onApprove, onIgnore, onPostSiloResponse, onRedraft, onCreateCustomNudge, onCreateCustomOp, onDeleteCustomNudge, onEditCustomNudge, onOpenLlmPicker, activeLlmOverride, onRecapture, hasResponseBelow, siloSlug, familyId, familyNudge, familyLength, onStageFamilyNudge, onStageFamilyLength, minimizeSignal, minimizeStage = 2, familyTimestamp, searchQuery, searchInfo, revealTargetId, revealNonce, activeGuidance, onEditGuidance, onClearGuidance }) {  if (entry.kind === "transcript") return <TranscriptCard entry={entry} sessionId={sessionId} nudges={nudges} commands={commands} busy={busy} redraftArmed={redraftArmed} onOp={onOp} onAskSilo={onAskSilo} onSaveEdit={onSaveEdit} onDelete={onDelete} onCreateCustomNudge={onCreateCustomNudge} onCreateCustomOp={onCreateCustomOp} onDeleteCustomNudge={onDeleteCustomNudge} onEditCustomNudge={onEditCustomNudge} onOpenLlmPicker={onOpenLlmPicker} activeLlmOverride={activeLlmOverride} onRecapture={onRecapture} hasResponseBelow={hasResponseBelow} siloSlug={siloSlug} familyId={familyId} familyNudge={familyNudge} familyLength={familyLength} onStageFamilyNudge={onStageFamilyNudge} onStageFamilyLength={onStageFamilyLength} minimizeSignal={minimizeSignal} minimizeStage={minimizeStage} searchQuery={searchQuery} searchInfo={searchInfo} revealTargetId={revealTargetId} revealNonce={revealNonce} activeGuidance={activeGuidance} onEditGuidance={onEditGuidance} onClearGuidance={onClearGuidance} onOpenRedraftPad={onOpenRedraftPad} />;
  if (entry.kind === "silo_response") return <SiloResponseCard entry={entry} busy={busy} commands={commands} onOp={onOp} redraftArmed={redraftArmed} onSaveEdit={onSaveEdit} onDelete={onDelete} onOpenRedraftPad={onOpenRedraftPad} onRedraftSiloResponse={onRedraftSiloResponse} onPostSiloResponse={onPostSiloResponse} siloSlug={siloSlug} familyId={familyId} familyNudge={familyNudge} familyLength={familyLength} onStageFamilyNudge={onStageFamilyNudge} onStageFamilyLength={onStageFamilyLength} nudges={nudges} onCreateCustomNudge={onCreateCustomNudge} onCreateCustomOp={onCreateCustomOp} onDeleteCustomNudge={onDeleteCustomNudge} onEditCustomNudge={onEditCustomNudge} minimizeSignal={minimizeSignal} minimizeStage={minimizeStage} familyTimestamp={familyTimestamp} searchQuery={searchQuery} searchInfo={searchInfo} revealTargetId={revealTargetId} revealNonce={revealNonce} activeGuidance={activeGuidance} onEditGuidance={onEditGuidance} onClearGuidance={onClearGuidance} />;
  if (entry.kind === "user_comment") return <UserCommentCard entry={entry} busy={busy} onSaveEdit={onSaveEdit} onDelete={onDelete} nudges={nudges} siloSlug={siloSlug} siloId={siloId} onRedraftFromUserComment={onRedraftFromUserComment} onApprove={onApprove} onIgnore={onIgnore} onRedraft={onRedraft} familyId={familyId} familyNudge={familyNudge} familyLength={familyLength} onStageFamilyNudge={onStageFamilyNudge} onStageFamilyLength={onStageFamilyLength} onCreateCustomNudge={onCreateCustomNudge} onCreateCustomOp={onCreateCustomOp} onDeleteCustomNudge={onDeleteCustomNudge} onEditCustomNudge={onEditCustomNudge} minimizeSignal={minimizeSignal} minimizeStage={minimizeStage} familyTimestamp={familyTimestamp} searchQuery={searchQuery} searchInfo={searchInfo} revealTargetId={revealTargetId} revealNonce={revealNonce} />;
  return null;
}


function TranscriptCard({ entry, sessionId, nudges, commands, busy, redraftArmed, onOp, onAskSilo, onSaveEdit, onDelete, onCreateCustomNudge, onCreateCustomOp, onDeleteCustomNudge, onEditCustomNudge, onOpenLlmPicker, activeLlmOverride, onRecapture, hasResponseBelow, siloSlug, familyId, familyNudge, familyLength, onStageFamilyNudge, onStageFamilyLength, minimizeSignal, minimizeStage = 2, searchQuery = "", searchInfo = null, revealTargetId = null, revealNonce = 0, activeGuidance = null, onEditGuidance = () => {}, onClearGuidance = () => {}, onOpenRedraftPad = () => {} }) {
  const [showRevised, setShowRevised] = useState(!!entry.transcript_revised);
  useEffect(() => { setShowRevised(!!entry.transcript_revised); }, [entry.transcript_revised, entry.revised_by_op_id]);
  // Iter160s (Randy pt-ytre-family-mirror) — nudge + length now live on
  // the FAMILY (this transcript + its children). Pull from the family
  // state passed down by YtreTab; write via onStageFamilyNudge /
  // onStageFamilyLength so every mirror in the family updates in lock-
  // step. Local `stagedNudge` / `pendingLength` were retired.
  const stagedNudge = familyNudge || null;
  const pendingLength = familyLength || null;
  const [nudgePopoverOpen, setNudgePopoverOpen] = useState(false);
  // Iter160s — 3-stage palindromic chevron (see hook above). Default
  // stage 1 (mid: two-line preview + footer visible) matches the
  // previous iter160e default. First click expands to full; keep
  // clicking to cycle 1→0→1→2→1→0→…
  const chev = useThreeStageChevron({ initialStage: 1 });
  // Iter172 (Randy pt-ytre-refresh-and-minimize, Feb 13, 2026) — when
  // the parent bumps minimizeSignal, collapse THIS card to header-only.
  // Skip the initial 0 signal so freshly-mounted cards don't auto-collapse.
  // Iter172 audit-3 (pt-ytre-minimize-all): parent Minimize all bumps
  // this tick; every card collapses to header-only. Skip the initial 0.
  useEffect(() => {
    if (minimizeSignal > 0) chev.setStage(minimizeStage);
  }, [minimizeSignal]);  // eslint-disable-line react-hooks/exhaustive-deps
  // Iter172 audit-7 (Randy pt-ytre-canvas-search, Feb 17, 2026) —
  // when this card is the search's reveal target AND currently in
  // stage-2 (header-only), expand to stage-1 so the match is visible.
  // If already stage-0 or stage-1, leave it alone (no forced expand).
  useEffect(() => {
    if (revealNonce > 0 && revealTargetId === entry.id && chev.isHeaderOnly) {
      chev.setStage(1);
    }
  }, [revealNonce]);  // eslint-disable-line react-hooks/exhaustive-deps
  const displayText = showRevised && entry.transcript_revised ? entry.transcript_revised : entry.transcript_raw;
  const timeLabel = entry.time_start_sec != null && entry.time_end_sec != null
    ? `${fmtTime(entry.time_start_sec)} – ${fmtTime(entry.time_end_sec)}`
    : "(no time window)";

  // Sep 30, 2026 (Randy — "it was a work in progress and had not been
  // submitted yet … not only was it not saved"). The Edit box used to be plain
  // React state, so a tab switch or a power cut took it with it. useCardDraft
  // mirrors keystrokes to localStorage and hands the text back on remount.
  // Iter172 (Randy pt-ytre-edit-full-open) — onStart forces the chevron
  // full-open so the textarea gets the whole card's vertical real estate.
  const editField = showRevised && entry.transcript_revised ? "transcript_revised" : "transcript_raw";
  const dr = useCardDraft({
    id: entry.id, baseline: displayText || "", field: editField,
    onStart: () => chev.setStage(0),
  });
  const { editing, draft, setDraft, recovered } = dr;
  const startEdit = dr.start;
  const cancelEdit = dr.discard;
  const commitEdit = () => dr.commit((text) => onSaveEdit({ [editField]: text }));

  const allOps = commands.map((c) => ({ id: c.id, name: c.name, is_global: c.is_global, instructions: c.instructions }));

  // React-to-transcript is only clickable when BOTH the family nudge and
  // length are staged. Randy's spec: "they are only locked if length or
  // mold silo reaction (one or both) is not assigned. If those are
  // assigned from anywhere because they all mirror each other within
  // the family, then the react to transcript or redraw buttons will be
  // clickable." Length staged = family length is a non-null preset id.
  // Nudge staged = family nudge has an id.
  const nudgeStaged = !!stagedNudge?.id;
  const lengthStaged = !!pendingLength;
  const reactReady = nudgeStaged && lengthStaged;
  const reactDisabledTitle = !nudgeStaged && !lengthStaged
    ? "Pick a Mold Silo Reaction and a Length first (they can be staged from any card in this transcript's family)."
    : !nudgeStaged
      ? "Pick a Mold Silo Reaction first (can be staged from any card in this family)."
      : "Pick a Length first (can be staged from any card in this family).";

  return (
    <div ref={chev.cardRef} data-ytre-entry-id={entry.id} className="border hairline rounded-lg bg-white" data-testid={`ytre-card-transcript-${entry.id}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b hairline bg-[#F7F5F0]">
        <button
          onClick={chev.cycle}
          data-testid={`ytre-transcript-collapse-${entry.id}`}
          className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-90"
          title={`Chevron cycles: full ↔ mid ↔ header-only (stage ${chev.stage + 1}/3)`}
        >
          {chev.isFull
            ? <CaretDown size={13} weight="bold" className="text-[#6B6A68] shrink-0" />
            : chev.isHeaderOnly
              ? <CaretRight size={13} weight="bold" className="text-[#6B6A68] shrink-0" />
              : <CaretDown size={13} weight="bold" className="text-[#6B6A68] shrink-0 opacity-60" />}
          <PlayCircle size={13} weight="duotone" className="text-[#4A5D4E] shrink-0" />
          <span className="text-[11.5px] font-medium text-[#1C1B1A]">Transcript · {timeLabel}</span>
          {/* Feb 2026 (Randy pt-ytre-p1 fix2 · iter169-Phase1) — placeholder
              badge when the parent transcript was auto-created without
              a found timestamp. Prompts the user to run Re-capture. */}
          {entry.no_timestamp_found && (
            <span data-testid={`ytre-transcript-no-timestamp-${entry.id}`} className="ml-1 text-[9.5px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#FBE9E1] text-[#8C4A2E] border border-[#E5C4B2]">
              no timestamp — recapture to fill
            </span>
          )}
          {entry.revised_by_op_name && (
            <span className="text-[10px] text-[#6B6A68] italic ml-2 truncate">
              Revised by &ldquo;{entry.revised_by_op_name}&rdquo;
            </span>
          )}
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {entry.transcript_revised && (
            <button onClick={() => setShowRevised((v) => !v)} className="text-[10.5px] px-2 py-0.5 rounded border border-[#E4E6E1] hover:bg-white" data-testid={`ytre-transcript-toggle-${entry.id}`}>
              {showRevised ? "Show raw" : "Show revised"}
            </button>
          )}
          {!editing && (
            <>
              {/* Sep 11 2026 (Randy) — auto-suggest matches this card's
                  COMMENTS against the transcript, so a childless card (a
                  fresh Capture Point) has nothing to match against.
                  `hasResponseBelow` is already exactly "has any
                  user_comment or silo_response child". */}
              <RecaptureButton
                entryId={entry.id}
                sessionId={sessionId}
                currentAnchor={entry.anchor_sec}
                currentStart={entry.time_start_sec}
                currentEnd={entry.time_end_sec}
                onRecapture={(startSec, endSec) => onRecapture(entry.id, startSec, endSec)}
                disabled={busy}
                hasChildren={hasResponseBelow}
                testIdPrefix="ytre-transcript-recapture"
              />
              <button onClick={startEdit} className="p-1 rounded hover:bg-white" title="Edit" data-testid={`ytre-transcript-edit-${entry.id}`}>
                <PencilSimple size={12} weight="duotone" />
              </button>
            </>
          )}
          <button onClick={onDelete} className="p-1 rounded hover:bg-[#F5E5E1] text-[#C16E5A]" title="Delete" data-testid={`ytre-transcript-delete-${entry.id}`}>
            <Trash size={12} weight="duotone" />
          </button>
        </div>
      </div>

      {/* Body + footer are wrapped in animated grid-template-rows
          containers (Iter160t, Randy pt-ytre-chevron-no-jump) so the
          transition between stages feels fluid rather than jerky. When
          stage 2 (header-only) is active, both wrappers collapse to
          0fr with a 180ms ease. */}
      {/* BODY */}
      <div
        className="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out"
        style={{ gridTemplateRows: (editing || !chev.isHeaderOnly) ? "1fr" : "0fr" }}
      >
        <div className="min-h-0">
          {editing ? (
            <div className="p-3">
              {recovered && (
                <DraftRecoveredBanner entryId={entry.id} at={recovered.at} onRevert={dr.revertToSaved} />
              )}
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                rows={Math.min(20, Math.max(5, (draft || "").split("\n").length + 1))}
                data-testid={`ytre-transcript-editor-${entry.id}`}
                className="w-full border border-[#D8D6D2] rounded p-2 text-[12px] font-mono bg-white" />
              <div className="flex justify-end gap-2 mt-2">
                <button onClick={cancelEdit} data-testid={`ytre-transcript-edit-cancel-${entry.id}`} className="text-[11px] px-3 py-1 rounded text-[#6B6A68] hover:bg-[#F0F1EE]">Cancel</button>
                <button onClick={commitEdit} className="text-[11px] px-3 py-1 rounded bg-[#2F6B3A] text-white hover:bg-[#245029] flex items-center gap-1"><FloppyDisk size={11} weight="bold" /> Save</button>
              </div>
            </div>
          ) : chev.isFull ? (
            <div className="p-3">
              <pre className="whitespace-pre-wrap text-[12px] text-[#1C1B1A] font-sans leading-relaxed">
                {searchQuery
                  ? renderWithMarks(displayText || "", searchQuery, { activeIdx: searchInfo?.activeOccIdx, entryId: entry.id })
                  : displayText}
              </pre>
            </div>
          ) : (
            <div
              className="p-3 text-[12px] text-[#4A5D4E] italic cursor-pointer leading-relaxed"
              style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
              onClick={chev.cycle}
              data-testid={`ytre-transcript-preview-${entry.id}`}
              title="Click to cycle chevron"
            >
              {searchQuery && displayText
                ? renderWithMarks(displayText, searchQuery, { activeIdx: searchInfo?.activeOccIdx, entryId: entry.id })
                : (displayText || "(empty transcript)")}
            </div>
          )}
        </div>
      </div>

      {/* FOOTER — same collapse pattern. Hidden when editing OR when
          the chevron cycles to header-only stage. */}
      <div
        className="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out"
        style={{ gridTemplateRows: (!editing && !chev.isHeaderOnly) ? "1fr" : "0fr" }}
      >
        <div className="min-h-0">
        <div className="px-3 py-2 border-t hairline bg-[#FBF9F4] flex flex-wrap items-center gap-2">
          <OpDropdown ops={allOps} onPick={onOp} disabled={busy} onCreateCustom={onCreateCustomOp} />
          <AskSiloNudgeStager
            nudges={nudges}
            staged={stagedNudge}
            onStage={(nudge) => { onStageFamilyNudge(nudge); setNudgePopoverOpen(false); }}
            open={nudgePopoverOpen}
            onOpenChange={setNudgePopoverOpen}
            onCreateCustom={onCreateCustomNudge}
            onDeleteCustom={onDeleteCustomNudge}
            onEditCustom={onEditCustomNudge}
            disabled={busy}
            entryId={entry.id}
          />
          <button
            type="button"
            onClick={() => {
              if (!reactReady) return;
              // Feb 28 2026 (Randy pt-ytre-react-two-stage) — two-stage
              // React to transcript. First tap opens the capture pad
              // (Randy: "the first time you press it, it pops our
              // coveted interjection pop-up that we see when
              // initiating a re-draft"). Second tap (after pad
              // closes → armed) actually fires the react with any
              // guidance the user typed in.
              if (redraftArmed) {
                const moldingHint = activeGuidance?.text || null;
                onAskSilo(stagedNudge.id, stagedNudge.label, pendingLength, moldingHint);
                // One-shot guidance clears after react; sticky guidance
                // (persist=true) is preserved on the chip so the user
                // can re-fire with the same steer.
                if (activeGuidance && !activeGuidance.persist) onClearGuidance();
              } else {
                onOpenRedraftPad();
              }
            }}
            disabled={busy || !reactReady}
            data-testid={`ytre-transcript-execute-${entry.id}`}
            title={!reactReady
              ? reactDisabledTitle
              : redraftArmed
                ? "React now — fires with any guidance you typed."
                : hasResponseBelow
                  ? "React again — opens the guidance pad first, then generates another silo comment."
                  : "React — opens the guidance pad first, then generates a silo comment for this transcript slice."}
            className={`text-[11.5px] px-3 py-1.5 rounded-md flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed font-medium ${
              redraftArmed
                ? "bg-[#4A7A5A] text-white hover:bg-[#3A6A4A]"
                : hasResponseBelow
                  ? "bg-[#8A6A3A] text-white hover:bg-[#6E5330]"
                  : "bg-[#2F6B3A] text-white hover:bg-[#245029]"
            }`}
          >
            {redraftArmed ? <MagicWand size={11} weight="bold" /> : <ChatCircleText size={11} weight="bold" />}
            {redraftArmed ? "React now" : "React to transcript"}
          </button>
          {/* Feb 28 2026 (Randy pt-ytre-react-two-stage) — chip mirrors
              the redraft flow. Rendered even for TranscriptCard so
              Randy sees his guidance is armed on first-response too. */}
          <GuidanceChip
            guidance={activeGuidance}
            onEdit={onEditGuidance}
            onClear={onClearGuidance}
            testId={`transcript-${entry.id}`}
          />
          <div className="ml-auto flex items-center gap-2">
            <LengthStagePicker
              slug={siloSlug}
              staged={pendingLength}
              onStage={(id) => onStageFamilyLength(id)}
              disabled={false}
              entryId={entry.id}
            />
            <button
              type="button"
              onClick={onOpenLlmPicker}
              disabled={busy}
              data-testid={`ytre-transcript-llm-picker-${entry.id}`}
              title={activeLlmOverride?.model
                ? `Session model: ${activeLlmOverride.model}. Tap to change or reset.`
                : "Pick a different LLM for this tab-session (default: silo's configured model)."}
              className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-md border hairline bg-white text-[#4A5D4E] hover:border-[#4A5D4E] hover:bg-[#F0F1EE]/60 flex items-center gap-1 disabled:opacity-40"
            >
              <SlidersHorizontal size={11} weight="bold" />
              Model{activeLlmOverride?.model ? `: ${labelForModel(activeLlmOverride.model)}` : ""}
            </button>
          </div>
          {busy && <span className="text-[11px] text-[#6B6A68] italic ml-2"><ArrowsClockwise size={10} className="inline animate-spin mr-1" /> working…</span>}
        </div>
        </div>
      </div>
    </div>
  );
}


function OpDropdown({ ops, onPick, disabled, label = "Mold transcript", onCreateCustom }) {
  // Iter160u (Randy pt-dropdown-clip-fix, Feb 2026) — converted to Radix
  // Popover so the menu content portals to <body>, escaping the parent
  // card's `overflow-hidden` wrapper (the smooth-collapse grid animator).
  // Before this fix, opening Mold transcript from the transcript footer
  // showed the menu behind the child comment card because the parent
  // grid's overflow-hidden clipped it.
  //
  // Oct 2026 (Randy pt-ytre-op-on-response) — `label` added so the SAME
  // component serves the silo-response footer. Randy: "simply just act in
  // the exact same way that it currently exists on the parent." Shared
  // component, not a copy, so the ops list and the Custom badge can never
  // drift between the two footers.
  //
  // Jun 2026 (Randy) — the names alone never said what an operation DOES:
  // *"hover over on desktop, each operation shows the text details of the
  // operation. on mobile since you cannot hover over, tap once brings up the
  // text details, tap twice chooses it."* The details print in a panel pinned
  // to the TOP of the menu rather than in a floating tooltip, because a
  // tooltip over a scrolling list covers the next row you were about to read.
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  // Touch only: the row has been read once and the next tap on it runs it.
  const [armedId, setArmedId] = useState(null);
  const touch = typeof window !== "undefined" && !!window.matchMedia
    && window.matchMedia("(hover: none)").matches;
  useEffect(() => { if (!open) { setDetailId(null); setArmedId(null); } }, [open]);

  const shownId = detailId || armedId;
  const shown = ops.find((o) => o.id === shownId);

  const choose = (op) => {
    // Tap one reads, tap two runs — and only on a device that cannot hover,
    // so a mouse still fires on the first click exactly as it always has.
    if (touch && armedId !== op.id) { setArmedId(op.id); setDetailId(op.id); return; }
    setOpen(false);
    onPick(op.id, op.name);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          data-testid="ytre-op-dropdown-btn"
          className="text-[11.5px] px-3 py-1.5 rounded-md bg-white border border-[#E4E6E1] hover:bg-[#F0F1EE]/60 flex items-center gap-1.5 disabled:opacity-40"
        >
          <Sparkle size={11} weight="duotone" /> {label} {open ? <CaretUp size={10} /> : <CaretDown size={10} />}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-80 bg-white border border-[#E4E6E1] rounded-md shadow-lg py-0 p-0 z-[80] overflow-hidden"
        data-testid="ytre-op-dropdown-list"
      >
        {/* Jun 2026 (Randy): the detail panel is a FIXED height on purpose. It
            used to grow with the text, which moved the rows under the cursor,
            re-fired mouseenter on a different row, and strobed. Fixed box +
            its own scroll = the list below never shifts. */}
        <div className="h-[6.5rem] overflow-y-auto px-3 py-2 border-b border-[#E4E6E1] bg-white">
          <p
            data-testid="ytre-op-detail"
            className={`text-[10.5px] leading-snug ${shown ? "text-[#1C1B1A]" : "text-[#9B9992] italic"}`}
          >
            {shown
              ? (shown.instructions || "No details were written for this one.")
              : (touch
                  ? "Tap an operation once to read what it does, twice to run it."
                  : "Hover an operation to read what it does.")}
          </p>
          {shown && touch && (
            <p className="mt-1 text-[10px] text-[#2F6B3A]" data-testid="ytre-op-tap-again">
              Tap “{shown.name}” again to run it.
            </p>
          )}
        </div>
        <div className="max-h-56 overflow-y-auto">
          {ops.length === 0 && <div className="px-3 py-2 text-[11px] text-[#6B6A68]">No operations yet — make your first one below.</div>}
          {ops.map((op) => (
            <button key={op.id} onClick={() => choose(op)}
              onMouseEnter={() => setDetailId(op.id)}
              onFocus={() => setDetailId(op.id)}
              data-testid={`ytre-op-item-${op.id}`}
              data-armed={armedId === op.id ? "true" : "false"}
              className={`w-full text-left px-3 py-1.5 text-[11.5px] hover:bg-[#F0F1EE]/60 flex items-center justify-between ${
                armedId === op.id ? "bg-[#EAF3EC]" : ""
              } ${shownId === op.id ? "bg-[#F0F1EE]/60" : ""}`}>
              <span className="truncate">{op.name}</span>
              {!op.is_global && <span className="text-[9.5px] text-[#6B6A68] uppercase tracking-wide">Custom</span>}
            </button>
          ))}
        </div>
        <div className="border-t border-[#E4E6E1]">
          <button
            onClick={() => { setOpen(false); onCreateCustom && onCreateCustom(); }}
            data-testid="ytre-op-create-custom"
            className="w-full text-left px-3 py-2 flex items-center gap-2 text-[#2F6B3A] hover:bg-[#EAF3EC]"
          >
            <span className="text-[13px] leading-none">+</span>
            <span className="text-[11.5px] font-medium">Create custom operation</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


function AskSiloNudgeStager({ nudges, staged, onStage, open, onOpenChange, onCreateCustom, onDeleteCustom, onEditCustom, disabled, entryId }) {
  // Iter160g (Randy pt-ytre-p2 clarification) — this is a STAGER, not
  // an executor. Picking a nudge fills the pill; the transcript card's
  // separate execute button fires the /ask-silo POST. Shares its
  // open/onOpenChange state with the parent so a Length-popover open
  // doesn't overlap the nudge popover.
  const items = useMemo(() => nudges.map((n) => ({
    id: n.id,
    label: n.name,
    description: (n.instructions || "").slice(0, 90),
    is_custom: !n.is_global,
  })), [nudges]);
  const displayLabel = staged?.label || "Mold silo reaction";
  const isStaged = !!staged?.id;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          data-testid={`ytre-ask-silo-stager-btn-${entryId}`}
          title={isStaged
            ? `Staged reaction: ${displayLabel}. Tap React to transcript to fire it.`
            : "Pick a Mold Silo Reaction — the stance / voice that guides how the silo will respond to this transcript. Mirrored across every card in this family."}
          className={`text-[11.5px] px-3 py-1.5 rounded-md border flex items-center gap-1.5 disabled:opacity-40 ${
            isStaged
              ? "bg-[#DCEBDF] border-[#2F6B3A]/60 text-[#1C1B1A]"
              : "bg-[#EAF3EC] border-[#2F6B3A]/30 text-[#4A5D4E] hover:bg-[#DCEBDF]"
          }`}
        >
          <ChatCircleText size={11} weight="duotone" />
          {displayLabel}
          {open ? <CaretUp size={10} /> : <CaretDown size={10} />}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-80 max-h-72 overflow-y-auto bg-white border border-[#E4E6E1] rounded-md shadow-lg py-1 p-0 z-[80]"
        data-testid={`ytre-nudge-stage-list-${entryId}`}
      >
        {items.length === 0 && (
          <div className="px-3 py-4 text-center text-[11px] text-[#6B6A68] italic">
            No nudges available. Ask an admin to seed them.
          </div>
        )}
        {items.map((it) => {
          const isCurrent = staged?.id === it.id;
          return (
            <div key={it.id} className={`w-full flex items-stretch hover:bg-[#F0F1EE]/60 ${isCurrent ? "bg-[#F0F1EE]" : ""}`}>
              <button
                onClick={() => onStage({ id: it.id, label: it.label, is_custom: it.is_custom })}
                data-testid={`ytre-nudge-stage-item-${entryId}-${it.id}`}
                className="flex-1 text-left px-3 py-2 flex flex-col gap-0.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11.5px] font-medium text-[#1C1B1A]">{it.label}</span>
                  <div className="flex items-center gap-1.5">
                    {isCurrent && <span className="text-[10px] text-[#2F6B3A] font-medium">✓ staged</span>}
                    {it.is_custom && <span className="text-[9.5px] text-[#6B6A68] uppercase tracking-wide">Custom</span>}
                  </div>
                </div>
                {it.description && <span className="text-[10.5px] text-[#6B6A68] italic">{it.description}</span>}
              </button>
              {it.is_custom && (
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenChange(false); onEditCustom && onEditCustom(it.id); }}
                  className="px-2 text-[#6B6A68] hover:bg-[#F0F1EE] hover:text-[#1C1B1A]"
                  title="Edit this custom nudge"
                  data-testid={`ytre-nudge-stage-edit-${entryId}-${it.id}`}
                >
                  <PencilSimple size={11} weight="duotone" />
                </button>
              )}
              {it.is_custom && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteCustom && onDeleteCustom(it.id, it.label); }}
                  className="px-2 text-[#C16E5A] hover:bg-[#F5E5E1]"
                  title="Delete this custom nudge"
                  data-testid={`ytre-nudge-stage-delete-${entryId}-${it.id}`}
                >
                  <Trash size={11} weight="duotone" />
                </button>
              )}
            </div>
          );
        })}
        <div className="border-t hairline mt-1 pt-1">
          <button
            onClick={() => { onOpenChange(false); onCreateCustom && onCreateCustom(); }}
            data-testid={`ytre-nudge-stage-create-custom-${entryId}`}
            className="w-full text-left px-3 py-2 flex items-center gap-2 text-[#2F6B3A] hover:bg-[#EAF3EC]"
          >
            <span className="text-[13px] leading-none">+</span>
            <span className="text-[11.5px] font-medium">Create custom nudge</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


function SiloResponseCard({ entry, busy, commands = [], onOp = () => {}, redraftArmed, onSaveEdit, onDelete, onOpenRedraftPad, onRedraftSiloResponse, onPostSiloResponse, siloSlug, familyId, familyNudge, familyLength, onStageFamilyNudge, onStageFamilyLength, nudges, onCreateCustomNudge, onCreateCustomOp, onDeleteCustomNudge, onEditCustomNudge, minimizeSignal, minimizeStage = 2, familyTimestamp = null, searchQuery = "", searchInfo = null, revealTargetId = null, revealNonce = 0, activeGuidance = null, onEditGuidance = () => {}, onClearGuidance = () => {} }) {
  // Feb 26, 2026 (Randy pt-ytre-silo-response-post) — Post button state.
  // Mirrors UserCommentCard's approve/repost pattern: draft → "Post to
  // YouTube" pill → posting spinner → "Posted" pill. Post_state lives
  // on the entry (server-owned). Redrafting after posting isn't
  // supported yet on this flow — Randy can Delete and start fresh.
  const [posting, setPosting] = useState(false);
  const isPosted = entry.post_state === "posted";
  // Iter160s (Randy pt-ytre-family-mirror) → iter172 (Randy pt-ytre-per-
  // card-settings, Feb 13, 2026) — length + nudge are now PER-CARD
  // LOCAL state. The family-mirror is gone: Randy's audit found that
  // the parent-transcript nudge_label was never populated, so REACTION
  // MODE never fired on child re-drafts even though the pill LOOKED
  // active. Fix: each redraftable card owns its own Mold silo
  // reaction + Length. Passes them into the re-draft request; backend
  // scaffold reads the values via `nudge_override` + `length_override`
  // in `_build_child_context_prompt`.
  //
  // Reset behavior: React useState is naturally session-scoped —
  // a new URL load or new session unmounts the card and re-mounts
  // with `familyNudge` / `familyLength` initial values (both null in
  // practice for new sessions, so the pills read "Pure silo (no
  // nudge)" and "Standard").
  // Feb 27 2026 (Randy pt-ytre-card-settings-persist) — hydrate from
  // localStorage so tone/length/nudge choices survive refresh. Keyed
  // by entry.id (this specific silo comment card). Matches
  // UserCommentCard's hydration behavior.
  const _savedS = readCardSettings(entry.id);
  const [pendingLengthLocal, setPendingLengthLocal] = useState(_savedS?.length_id || familyLength || null);
  const pendingLength = pendingLengthLocal;
  const [stagedNudge, setStagedNudge] = useState(() => {
    if (_savedS?.nudge_id && Array.isArray(nudges)) {
      const found = nudges.find((n) => (n.id || n.command_id) === _savedS.nudge_id);
      if (found) return found;
    }
    return familyNudge || null;
  });
  // Iter172 (Randy pt-ytre-tone-picker, Feb 13, 2026) — per-card
  // Tone delivery. Now hydrates from localStorage.
  const [stagedTone, setStagedTone] = useState(_savedS?.tone_id ? { id: _savedS.tone_id } : null);
  useEffect(() => { writeCardSettings(entry.id, { length_id: pendingLengthLocal || null }); }, [entry.id, pendingLengthLocal]);
  useEffect(() => { writeCardSettings(entry.id, { nudge_id: (stagedNudge?.id || stagedNudge?.command_id) || null }); }, [entry.id, stagedNudge]);
  useEffect(() => { writeCardSettings(entry.id, { tone_id: stagedTone?.id || null }); }, [entry.id, stagedTone]);
  const [nudgePopoverOpen, setNudgePopoverOpen] = useState(false);
  const chev = useThreeStageChevron({ initialStage: 1 });
  // Sep 30, 2026 — the response you were part-way through rewriting now
  // survives a tab switch, a route change and a power cut (see useCardDraft).
  const dr = useCardDraft({
    id: entry.id, baseline: entry.silo_response_text || "", field: "silo_response_text",
    onStart: () => chev.setStage(0),
  });
  const { editing, draft, setDraft, recovered } = dr;
  // Iter172 audit-3 (pt-ytre-minimize-all): parent Minimize all bumps
  // this tick; every card collapses to header-only. Skip the initial 0.
  useEffect(() => {
    if (minimizeSignal > 0) chev.setStage(minimizeStage);
  }, [minimizeSignal]);  // eslint-disable-line react-hooks/exhaustive-deps
  // Iter172 audit-7 (Randy pt-ytre-canvas-search, Feb 17, 2026) — see
  // matching hook in TranscriptCard.
  useEffect(() => {
    if (revealNonce > 0 && revealTargetId === entry.id && chev.isHeaderOnly) {
      chev.setStage(1);
    }
  }, [revealNonce]);  // eslint-disable-line react-hooks/exhaustive-deps
  const color = colorForNudge(entry.nudge_command_id);
  // Feb 16 2026 (Randy pt-ytre-recapture-persist) — family timestamp
  // priority: prefer the PARENT transcript's current time_end_sec (via
  // the `familyTimestamp` prop) so a manual Recapture on the parent
  // instantly relabels every silo comment card in the family. Fall
  // back to the child's frozen anchor_sec.
  const anchor = (typeof familyTimestamp === "number" && familyTimestamp >= 0)
    ? familyTimestamp
    : entry.anchor_sec;
  const hasAnchor = typeof anchor === "number" && anchor >= 0;
  const anchorLabel = hasAnchor ? (() => {
    const s = Math.floor(anchor);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return h > 0
      ? `@ ${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`
      : `@ ${m}:${String(ss).padStart(2, "0")}`;
  })() : "";

  // Re-draft on this SPECIFIC silo comment uses the family recipe.
  // Locked until BOTH nudge + length are staged anywhere in the family.
  const nudgeStaged = !!stagedNudge?.id;
  const lengthStaged = !!pendingLength;
  const redraftReady = nudgeStaged && lengthStaged;
  const redraftDisabledTitle = !nudgeStaged && !lengthStaged
    ? "Pick a Mold Silo Reaction and a Length (they mirror across every card in this family)."
    : !nudgeStaged
      ? "Pick a Mold Silo Reaction (mirrors across every card in this family)."
      : "Pick a Length (mirrors across every card in this family).";

  return (
    <div ref={chev.cardRef} data-ytre-entry-id={entry.id} className="border border-t-0 hairline rounded-t-none rounded-b-lg bg-white" data-testid={`ytre-card-silo-${entry.id}`} style={{ borderLeftWidth: 4, borderLeftColor: color }}>
      <div className="flex items-center justify-between px-3 py-2 border-b hairline bg-[#F7FAF7]">
        <button
          onClick={chev.cycle}
          data-testid={`ytre-silo-collapse-${entry.id}`}
          className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-90"
          title={`Chevron cycles: full ↔ mid ↔ header-only (stage ${chev.stage + 1}/3)`}
        >
          {chev.isFull
            ? <CaretDown size={13} weight="bold" className="text-[#6B6A68] shrink-0" />
            : chev.isHeaderOnly
              ? <CaretRight size={13} weight="bold" className="text-[#6B6A68] shrink-0" />
              : <CaretDown size={13} weight="bold" className="text-[#6B6A68] shrink-0 opacity-60" />}
          <ChatCircleText size={13} weight="duotone" className="text-[#4A8060] shrink-0" />
          <span className="text-[11.5px] font-medium text-[#1C1B1A]">Silo comment</span>
          {hasAnchor && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded bg-[#EAF3EC] text-[#2F6B3A] font-mono"
              data-testid={`ytre-silo-anchor-${entry.id}`}
            >
              {anchorLabel}
            </span>
          )}
          {entry.nudge_label && (
            <span className="text-[10px] text-[#6B6A68] italic ml-1">· {entry.nudge_label}</span>
          )}
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {!editing && (
            <button onClick={dr.start} className="p-1 rounded hover:bg-white" title="Edit" data-testid={`ytre-silo-edit-${entry.id}`}>
              <PencilSimple size={12} weight="duotone" />
            </button>
          )}
          <button onClick={onDelete} className="p-1 rounded hover:bg-[#F5E5E1] text-[#C16E5A]" title="Delete" data-testid={`ytre-silo-delete-${entry.id}`}>
            <Trash size={12} weight="duotone" />
          </button>
        </div>
      </div>

      {/* Body — animated grid-template-rows collapse (Iter160t no-jump). */}
      <div
        className="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out"
        style={{ gridTemplateRows: (editing || !chev.isHeaderOnly) ? "1fr" : "0fr" }}
      >
        <div className="min-h-0">
          {editing ? (
            <div className="p-3">
              {recovered && (
                <DraftRecoveredBanner entryId={entry.id} at={recovered.at} onRevert={dr.revertToSaved} />
              )}
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                rows={Math.min(20, Math.max(5, (draft || "").split("\n").length + 1))}
                data-testid={`ytre-silo-editor-${entry.id}`}
                className="w-full border border-[#D8D6D2] rounded p-2 text-[12px] bg-white" />
              <div className="flex justify-end gap-2 mt-2">
                <button onClick={dr.discard} data-testid={`ytre-silo-edit-cancel-${entry.id}`} className="text-[11px] px-3 py-1 rounded text-[#6B6A68] hover:bg-[#F0F1EE]">Cancel</button>
                <button onClick={() => dr.commit((text) => onSaveEdit({ silo_response_text: text }))} data-testid={`ytre-silo-edit-save-${entry.id}`} className="text-[11px] px-3 py-1 rounded bg-[#2F6B3A] text-white hover:bg-[#245029] flex items-center gap-1"><FloppyDisk size={11} weight="bold" /> Save</button>
              </div>
            </div>
          ) : chev.isFull ? (
            <div className="p-3">
              <p className="whitespace-pre-wrap text-[12.5px] text-[#1C1B1A] leading-relaxed">
                {searchQuery && entry.silo_response_text
                  ? renderWithMarks(entry.silo_response_text, searchQuery, { activeIdx: searchInfo?.activeOccIdx, entryId: entry.id })
                  : entry.silo_response_text}
              </p>
            </div>
          ) : (
            <div
              className="p-3 text-[12px] text-[#1C1B1A] cursor-pointer leading-relaxed"
              style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
              onClick={chev.cycle}
              data-testid={`ytre-silo-preview-${entry.id}`}
              title="Click to cycle chevron"
            >
              {searchQuery && entry.silo_response_text
                ? renderWithMarks(entry.silo_response_text, searchQuery, { activeIdx: searchInfo?.activeOccIdx, entryId: entry.id })
                : (entry.silo_response_text || "(empty silo comment)")}
            </div>
          )}
        </div>
      </div>

      {/* Footer — Iter172 (Randy pt-ytre-per-card-settings, Feb 13, 2026).
          Per-card local Mold reaction + Re-draft on the left, per-card
          local Length on the far right (blue, mirrors transcript card). */}
      <div
        className="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out"
        style={{ gridTemplateRows: (!editing && !chev.isHeaderOnly && entry.source_entry_id) ? "1fr" : "0fr" }}
      >
        <div className="min-h-0">
          <div className="px-3 py-2 border-t hairline bg-[#F4F8F5] flex items-center gap-2 flex-wrap">
            {/* Oct 2026 (Randy pt-ytre-op-on-response) — the Workbench op
                dropdown, replicated from the transcript (parent) footer.
                Randy: "It's ultimately just replication of the drop down that
                currently exists in the workbench... Even though most of those
                operations will not be used. It still recognizes the customize
                created." Same component, same ops list, same Custom badge —
                the point is that a custom command he builds in the Workbench
                shows up here without any further wiring. */}
            <OpDropdown
              ops={(commands || []).map((c) => ({ id: c.id, name: c.name, is_global: c.is_global, instructions: c.instructions }))}
              onPick={onOp}
              disabled={busy}
              label="Mold response"
              onCreateCustom={onCreateCustomOp}
            />
            {/* The response has one text field, so an op overwrites it. This
                pill is this card's equivalent of the transcript's Raw/Revised
                toggle — it self-hides once the text matches the pre-op value
                again, which is what makes Revert need no extra endpoint. */}
            {!!entry.silo_response_text_pre_op
              && entry.silo_response_text !== entry.silo_response_text_pre_op && (
              <span
                data-testid={`ytre-silo-op-applied-${entry.id}`}
                className="text-[10.5px] px-2 py-1 rounded-md bg-white border border-[#E4E6E1] text-[#6B6A68] flex items-center gap-1.5"
              >
                <Sparkle size={10} weight="duotone" />
                {entry.revised_by_op_name || "Operation"} applied
                <button
                  onClick={() => onSaveEdit({ silo_response_text: entry.silo_response_text_pre_op })}
                  disabled={busy}
                  data-testid={`ytre-silo-op-revert-${entry.id}`}
                  title="Put the text back the way it was before this operation ran."
                  className="underline hover:text-[#4A5D4E] disabled:opacity-40"
                >
                  Revert
                </button>
              </span>
            )}
            <AskSiloNudgeStager
              nudges={nudges}
              staged={stagedNudge}
              onStage={(nudge) => { setStagedNudge(nudge); setNudgePopoverOpen(false); }}
              open={nudgePopoverOpen}
              onOpenChange={setNudgePopoverOpen}
              onCreateCustom={onCreateCustomNudge}
              onDeleteCustom={onDeleteCustomNudge}
              onEditCustom={onEditCustomNudge}
              disabled={false}
              entryId={`silo-${entry.id}`}
            />
            {/* Iter172 (Randy pt-ytre-tone-picker, Feb 13, 2026) —
                Tone delivery picker sits IMMEDIATELY right of the
                Mold silo reaction pill (Randy's spec). Local per-
                card state — sent up on redraft as `tone_id`. */}
            <TonePickerPill
              staged={stagedTone}
              onStage={setStagedTone}
              disabled={false}
              entryId={`silo-${entry.id}`}
            />
            <button
              onClick={() => {
                if (!redraftReady) return;
                if (redraftArmed) onRedraftSiloResponse(pendingLength, stagedNudge, stagedTone);
                else onOpenRedraftPad();
              }}
              disabled={busy || !redraftReady}
              data-testid={`ytre-silo-redraft-${entry.id}`}
              title={!redraftReady
                ? redraftDisabledTitle
                : redraftArmed
                  ? "Silo memory updated — tap again to overwrite this comment with the fresh Mold reaction + Length staged on THIS card."
                  : "Re-draft this comment using the Mold Silo Reaction + Length staged on THIS card. Overwrites this card only."}
              className={`text-[11.5px] px-3 py-1.5 rounded-md border flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed ${
                redraftArmed && redraftReady
                  ? "bg-[#2F6B3A] text-white border-[#2F6B3A] hover:bg-[#245029]"
                  : "bg-white border-[#E4E6E1] text-[#4A5D4E] hover:bg-[#F0F1EE]/60"
              }`}
            >
              {redraftArmed && redraftReady ? <MagicWand size={11} weight="bold" /> : <ArrowsClockwise size={11} weight="duotone" />}
              {redraftArmed && redraftReady ? "Re-draft now" : "Re-draft"}
            </button>
            {/* Feb 27 2026 (Randy pt-ytre-guide-chip) — visible chip
                so Randy can *see* his guidance is armed. Click chip
                body to reopen the pad in edit mode; X to clear.
                Rendered only when guidance is set for this entry. */}
            <GuidanceChip
              guidance={activeGuidance}
              onEdit={onEditGuidance}
              onClear={onClearGuidance}
              testId={`silo-${entry.id}`}
            />
            {/* Feb 26, 2026 (Randy pt-ytre-silo-response-post) — Post
                to YouTube button. Feb 28 2026 (Randy pt-ytre-silo-
                response-repost) — extended to mirror UserCommentCard's
                three-state pattern:
                  • draft (never posted)                 → blue "Post to YouTube"
                  • posted + clean (body unchanged)      → green "Posted" chip
                  • posted + dirty (edited or redrafted) → orange "Re-post" (mode="update")
                `bodyIsDirty` compares the live body against `last_posted_body`
                (backfilled on edit/redraft by patch_entry / ask-silo). */}
            {onPostSiloResponse && !editing && (() => {
              const currentBody = entry.silo_response_text || "";
              const baselineBody = entry.last_posted_body != null
                ? entry.last_posted_body
                : currentBody;
              const bodyIsDirty = currentBody.trim() !== baselineBody.trim();
              if (isPosted && !bodyIsDirty) {
                return (
                  <span
                    data-testid={`ytre-silo-posted-pill-${entry.id}`}
                    className="text-[11.5px] px-3 py-1.5 rounded-md bg-[#EAF3EC] text-[#2F6B3A] border border-[#4A5D4E] flex items-center gap-1"
                    title="This comment is live on YouTube."
                  >
                    <Check size={11} weight="bold" /> Posted
                  </span>
                );
              }
              const isRepost = isPosted && bodyIsDirty;
              return (
                <button
                  onClick={async () => {
                    if (posting) return;
                    setPosting(true);
                    try { await onPostSiloResponse(entry, isRepost ? { mode: "update" } : {}); }
                    finally { setPosting(false); }
                  }}
                  disabled={posting || busy}
                  data-testid={`ytre-silo-${isRepost ? "repost" : "post"}-btn-${entry.id}`}
                  title={isRepost
                    ? "Overwrite the existing YouTube comment with this edited/re-drafted version."
                    : "Post this silo comment to YouTube as a new top-level comment on the video."}
                  className={`text-[11.5px] px-3 py-1.5 rounded-md text-white flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed ${
                    isRepost
                      ? "bg-[#C16E5A] hover:bg-[#A85A47] border border-[#C16E5A]"
                      : "bg-[#2C6E92] hover:bg-[#245878] border border-[#2C6E92]"
                  }`}
                >
                  {posting ? <ArrowsClockwise size={11} weight="bold" className="animate-spin" /> : <Check size={11} weight="bold" />}
                  {posting
                    ? (isRepost ? "Re-posting…" : "Posting…")
                    : (isRepost ? "Re-post" : "Post to YouTube")}
                </button>
              );
            })()}
            {busy && <span className="text-[11px] text-[#6B6A68] italic"><ArrowsClockwise size={10} className="inline animate-spin mr-1" /> working…</span>}
            <div className="ml-auto">
              <LengthStagePicker
                slug={siloSlug}
                staged={pendingLength}
                onStage={(id) => setPendingLengthLocal(id)}
                disabled={false}
                entryId={`silo-${entry.id}`}
                promptLabel="Length for THIS card's next re-draft (independent — does not affect siblings)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────
// Master Reply Modal — Iter160b.
//
// Compiles the entire session into ONE publish-ready YouTube comment.
// Reuses the YT Replies pipeline patterns:
//   • Length dropdown = staging picker. Picking a preset stages it for
//     the next Generate / Re-draft; label reflects the pick.
//   • Re-draft = capture-pad-first (armed pill turns green when pad
//     closes; second tap actually re-runs /master-reply through the
//     ask_silo pipeline so any new silo memory is baked in).
//   • Send to Silo = shared DocSaveDialog (same UX every other Push-to-
//     silo uses).
//   • Send to Workbench = appends to the silo's workbench staging.
//   • Post to YouTube = /post endpoint via the silo's channel1 OAuth.
// ─────────────────────────────────────────────────────────
function MasterReplyModal({ session, siloId, siloName, siloSlug, allSilos, onClose, onPatchSession }) {
  const initialDraft = session.master_reply_draft || "";
  // Iter160l (Randy pt-length-picker-unification finish, Feb 2026) —
  // migrated length pill to <LengthStagePicker>. `null` = "use silo
  // default"; the picker resolves that to the owner's configured
  // preset. Backend accepts server preset ids AND legacy short/medium/
  // long values, so old saved sessions with `master_reply_length="medium"`
  // still work end-to-end even though the pill will now show the silo
  // default label instead of "Medium".
  const initialLength = session.master_reply_length || null;

  // Draft body — starts from the persisted draft, editable inline.
  const [draft, setDraft] = useState(initialDraft);
  const [busy, setBusy] = useState(false);
  // Feb 28 2026 v2 (master agent audit) — per-family progress from
  // the async job. Rendered inside the busy state so a 2-minute
  // master-reply run feels intentional ("compiling family 4 of 12")
  // instead of a silent multi-minute spinner.
  const [busyProgress, setBusyProgress] = useState(null);
  const [posting, setPosting] = useState(false);
  // Iter171 (Randy pt-master-reply-v2) — instruction textarea + length
  // picker were removed from the modal because the new whole-breakdown
  // pipeline doesn't take a length-target or free-form directive. The
  // per-family LLM calls are self-scoped. `signature` stays because
  // it still appends to the end of the breakdown for Post-to-YouTube.
  const instruction = "";
  const [signature, setSignature] = useState("");
  // Length hint (legacy): the backend still accepts it for callers
  // that mix new & old flows. We default to null → backend uses medium.
  const pendingLength = initialLength;
  // Iter171 (Randy pt-master-reply-v2, Feb 13, 2026) — single toggle
  // for the new whole-breakdown pipeline. false = verbatim (full
  // append); true = LLM-condensed owner responses. Persists on the
  // session across regenerates so the pill remembers Randy's choice.
  const [summarizeResponses, setSummarizeResponses] = useState(
    session.master_reply_mode === "summarize"
  );
  // Re-draft state machine (mirrors YTDraftCard): "plain" | "armed".
  const [capturePadOpen, setCapturePadOpen] = useState(false);
  const [redraftState, setRedraftState] = useState("plain");
  const [pushDialogOpen, setPushDialogOpen] = useState(false);

  const openCapturePad = () => setCapturePadOpen(true);
  const closeCapturePad = (_didPush, _guidance, _persist) => {
    // Master-reply is a per-family batch compilation, not a single reply,
    // so "Guide this reply" doesn't map cleanly here — we accept the
    // guidance/persist params for signature compatibility but discard
    // them. (Randy Feb 27 2026 pt-ytre-guide-this-reply — card-level
    // surfaces only.)
    setCapturePadOpen(false);
    setRedraftState("armed");
    toast.success("Silo updated — tap Re-draft again to regenerate.");
  };

  const runGenerate = async () => {
    setBusy(true);
    try {
      // Iter160u (Randy pt-master-reply-fix, Feb 2026) — only send `length`
      // when the user has actually staged one. `pendingLength` starts as
      // `null` (meaning "use silo default"); the backend's
      // YtreMasterReplyRequest defines `length: str = "medium"` — a
      // required string with default. Sending `{ length: null }` failed
      // Pydantic validation with "Input should be a valid string" → the
      // toast Randy screenshotted. Fix: omit the key entirely when null,
      // let the backend default take over.
      const body = {};
      if (pendingLength) body.length = pendingLength;
      if (instruction.trim()) body.instruction = instruction.trim();
      if (signature.trim()) body.signature = signature.trim();
      // Iter171 (Randy pt-master-reply-v2) — pipe the toggle to the
      // new backend. false = full append verbatim; true = LLM-condense.
      body.summarize_responses = !!summarizeResponses;
      // Feb 2026 (Randy pt-ytre-async-jobs) — master-reply is the
      // highest-risk YTRE surface for the 60s ingress cutoff (per-
      // family loop on long videos easily exceeds it). Backend now
      // returns {job_id}; helper polls until the whole breakdown is
      // ready. Same result payload the sync endpoint used to return.
      //
      // Feb 28 2026 v2 (master agent audit) — subscribe to per-
      // family progress so the busy toast reflects real work
      // instead of just spinning.
      const data = await runYtreAsyncJob(
        `/ytre/sessions/${session.id}/master-reply`,
        body,
        {
          onProgress: (p) => {
            if (p && typeof p.done === "number" && typeof p.total === "number") {
              setBusyProgress({ done: p.done, total: p.total });
            }
          },
        },
      );
      setBusyProgress(null);
      setDraft(data.draft || "");
      setRedraftState("plain");
      onPatchSession({
        master_reply_draft: data.draft,
        master_reply_length: data.length,
        master_reply_mode: data.breakdown_mode,
        master_reply_family_count: data.family_count,
      });
      const familySuffix = data.family_count ? ` · ${data.family_count} famil${data.family_count === 1 ? "y" : "ies"}` : "";
      toast.success(`Whole breakdown compiled (${data.word_count} words${familySuffix}).`);
    } catch (e) {
      toast.error(friendlyPipelineError(e, "Master reply failed."));
    } finally {
      setBusy(false);
      setBusyProgress(null);
    }
  };

  // Persist inline edits to the draft (PATCH, no LLM call).
  const saveEdits = async () => {
    try {
      await api.patch(`/ytre/sessions/${session.id}/master-reply`, { draft });
      onPatchSession({ master_reply_draft: draft });
      toast.success("Draft saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    }
  };

  const copyDraft = () => {
    if (!draft.trim()) return toast.error("Nothing to copy.");
    try { navigator.clipboard.writeText(draft); toast.success("Copied."); }
    catch { toast.error("Copy failed — select manually."); }
  };

  const sendToWorkbench = async () => {
    try {
      // Iter160r (Randy pt-ytre-send-workbench-realtime, Feb 2026) —
      // same real-time sync as the two-sided send: server appends,
      // response returns the new full staging + silo_id, we push those
      // into sessionStorage + dispatch `staging-updated` so an already-
      // open Workbench tab reflects the append without a page refresh.
      const r = await api.post(`/ytre/sessions/${session.id}/export/to-workbench`, {
        kind: "master",
        master_text: draft,
      });
      const nextStaging = r.data?.staging;
      const targetSilo = r.data?.silo_id || siloId;
      if (typeof nextStaging === "string" && targetSilo) {
        try { writeStaging(targetSilo, nextStaging); } catch { /* */ }
        try {
          window.dispatchEvent(new CustomEvent("staging-updated", { detail: { siloId: targetSilo } }));
        } catch { /* */ }
      }
      toast.success(`Sent to Workbench staging (+${(r.data?.appended_chars || 0).toLocaleString()} chars).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send to Workbench failed.");
    }
  };

  const openPushToSilo = () => {
    if (!draft.trim()) return toast.error("Nothing to send — generate the master reply first.");
    setPushDialogOpen(true);
  };

  const postToYoutube = async () => {
    if (!draft.trim()) return toast.error("Nothing to post.");
    const mode = session.original_comment_id ? "update" : "new";
    if (!window.confirm(`This will ${mode === "update" ? "UPDATE your existing comment" : "POST a new comment"} on YouTube. Continue?`)) return;
    setPosting(true);
    try {
      await api.post(`/ytre/sessions/${session.id}/post`, {
        comment_text: draft,
        mode,
      });
      toast.success(mode === "update" ? "Comment updated on YouTube." : "Comment posted to YouTube.");
      onPatchSession({ master_reply_posted_text: draft });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Post failed.");
    } finally {
      setPosting(false);
    }
  };

  const wordCount = draft.trim() ? draft.trim().split(/\s+/).length : 0;

  const silosForDialog = allSilos && allSilos.length
    ? allSilos.map((s) => ({ id: s.id, name: s.name }))
    : [{ id: siloId, name: siloName || "this silo" }];
  const defaultTitle = `YTRE — ${session.video_title || session.video_id}`;

  return (
    <div className="fixed inset-0 z-[230] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="ytre-master-reply-modal">
      <div className="bg-[#FDFCF8] rounded-2xl w-full max-w-2xl max-h-[92dvh] flex flex-col shadow-2xl border hairline overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b hairline shrink-0 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">Master Reply</div>
            <h2 className="font-serif-display text-lg text-[#1C1B1A] flex items-center gap-2 truncate">
              <MagicWand size={17} weight="duotone" className="text-[#4A5D4E]" />
              Whole breakdown
            </h2>
          </div>
          <button onClick={onClose} data-testid="ytre-master-close" className="p-1 rounded hover:bg-[#F0F1EE]">
            <X size={18} />
          </button>
        </div>

        {/* Controls row — Summarize toggle + optional length/signature/instruction */}
        <div className="px-5 pt-3 pb-2 space-y-2.5 shrink-0">
          {/* Iter171 (Randy pt-master-reply-v2, Feb 13, 2026) — single
              toggle in the modal. "Full append" leaves every owner
              response verbatim (default, preserves exact voice); tap
              the pill to switch to "Summarize" mode where each
              response is LLM-condensed to 1-3 sentences. Persists on
              the session so re-opening the modal remembers Randy's
              choice. */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border hairline bg-white overflow-hidden text-[10.5px] uppercase tracking-[0.14em]" data-testid="ytre-master-mode-toggle">
              <button
                type="button"
                onClick={() => setSummarizeResponses(false)}
                data-testid="ytre-master-mode-full"
                className={`px-3 py-1.5 transition ${!summarizeResponses ? "bg-[#1C1B1A] text-[#FDFCF8]" : "text-[#6B6A68] hover:bg-[#F4F3EE]"}`}
                title="Owner responses are kept verbatim (preserves exact voice)."
              >
                Full append
              </button>
              <button
                type="button"
                onClick={() => setSummarizeResponses(true)}
                data-testid="ytre-master-mode-summarize"
                className={`px-3 py-1.5 border-l hairline transition ${summarizeResponses ? "bg-[#1C1B1A] text-[#FDFCF8]" : "text-[#6B6A68] hover:bg-[#F4F3EE]"}`}
                title="LLM condenses each owner response to 1-3 sentences."
              >
                Summarize
              </button>
            </div>
            <input
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Optional signature (no URLs)"
              data-testid="ytre-master-signature"
              className="flex-1 min-w-[180px] text-[11.5px] px-3 py-1.5 border hairline rounded-md bg-white focus:outline-none focus:border-[#4A5D4E]"
              maxLength={140}
            />
          </div>
          {/* Oct 1, 2026 (Randy) — the two modes did the right thing and said
              nothing about it. The description of the SELECTED mode now sits
              under the pills, so the choice explains itself before Generate. */}
          <p className="text-[11px] leading-relaxed text-[#6B6A68]" data-testid="ytre-master-mode-help">
            {summarizeResponses ? (
              <><strong className="text-[#1C1B1A]">Summarize:</strong> condenses the transcript context <em>and</em> your answer for every parent/child pair. Shortest result, and your wording gets rewritten.</>
            ) : (
              <><strong className="text-[#1C1B1A]">Full append:</strong> condenses the transcript context but keeps your answer 100% intact, word for word, for every parent/child pair.</>
            )}
          </p>
        </div>

        {/* Draft body */}
        <div className="flex-1 overflow-y-auto px-5 py-3 min-h-[200px]">
          {!draft && !busy && (
            <div className="text-center py-10 text-[12.5px] text-[#6B6A68] italic border border-dashed border-[#D8D6D2] rounded-md">
              Pick <strong>Full append</strong> or <strong>Summarize</strong> above, then tap <strong>Generate</strong> to compile every family&apos;s transcript context + your response into one <strong>whole breakdown</strong>.
            </div>
          )}
          {draft && (
            <>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-1.5 flex items-center gap-2">
                Whole breakdown
                <span className="text-[10px] normal-case tracking-normal text-[#6B6A68] font-normal">· {wordCount} words</span>
              </div>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                data-testid="ytre-master-draft"
                rows={Math.min(20, Math.max(6, (draft.split("\n").length + 1)))}
                className="w-full px-3 py-3 border hairline rounded-md text-[13px] bg-white focus:outline-none focus:border-[#4A5D4E] resize-y leading-relaxed"
              />
              <div className="text-right pt-1">
                <button onClick={saveEdits} data-testid="ytre-master-save-edits" className="text-[10.5px] text-[#4A5D4E] hover:text-[#1C1B1A] underline">
                  Save my edits
                </button>
              </div>
            </>
          )}
        </div>

        {/* Actions row */}
        <div className="px-5 py-3 border-t hairline shrink-0 flex flex-wrap items-center gap-2 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))]">
          {/* Left cluster — generate/redraft */}
          {!draft ? (
            <button onClick={runGenerate} disabled={busy}
              data-testid="ytre-master-generate"
              className="text-[12.5px] px-4 py-2 rounded-md bg-[#2F6B3A] text-white hover:bg-[#245029] disabled:opacity-50 flex items-center gap-2 font-medium">
              {busy ? <ArrowsClockwise size={13} weight="bold" className="animate-spin" /> : <Sparkle size={13} weight="duotone" />}
              {busy
                ? (busyProgress
                    ? `Compiling family ${busyProgress.done} of ${busyProgress.total}…`
                    : "Compiling breakdown…")
                : "Generate whole breakdown"}
            </button>
          ) : (
            <button
              onClick={redraftState === "armed" ? runGenerate : openCapturePad}
              disabled={busy}
              data-testid="ytre-master-redraft"
              title={redraftState === "armed"
                ? "Silo memory was updated — click to regenerate the master reply."
                : "Open the capture pad to add silo memory, then re-draft."}
              className={`text-[12px] px-3 py-2 rounded-md flex items-center gap-1.5 disabled:opacity-50 ${
                redraftState === "armed"
                  ? "bg-[#2F6B3A] text-white hover:bg-[#245029]"
                  : "border hairline bg-white text-[#4A5D4E] hover:bg-[#F0F1EE]/60"
              }`}
            >
              {redraftState === "armed" ? <MagicWand size={12} weight="bold" /> : <ArrowsClockwise size={12} weight="duotone" />}
              {busy ? "Working…" : redraftState === "armed" ? "Re-draft now" : "Re-draft"}
            </button>
          )}

          <span className="flex-1" />

          {/* Right cluster — export + post */}
          {draft && (
            <>
              <button onClick={copyDraft} data-testid="ytre-master-copy"
                className="text-[11.5px] px-3 py-2 rounded-md border hairline bg-white text-[#4A5D4E] hover:bg-[#F0F1EE]/60 flex items-center gap-1.5">
                <CopyIcon size={11} weight="duotone" /> Copy
              </button>
              <button onClick={sendToWorkbench} data-testid="ytre-master-send-workbench"
                className="text-[11.5px] px-3 py-2 rounded-md border hairline bg-white text-[#4A5D4E] hover:bg-[#F0F1EE]/60 flex items-center gap-1.5">
                <ShareNetwork size={11} weight="duotone" /> Send to Workbench
              </button>
              <button onClick={openPushToSilo} data-testid="ytre-master-send-silo"
                className="text-[11.5px] px-3 py-2 rounded-md border hairline bg-white text-[#4A5D4E] hover:bg-[#F0F1EE]/60 flex items-center gap-1.5">
                <PaperPlaneRight size={11} weight="duotone" /> Send to Silo
              </button>
              <button onClick={postToYoutube} disabled={posting} data-testid="ytre-master-post"
                className="text-[12px] px-4 py-2 rounded-md bg-[#B54848] text-white hover:bg-[#8f3838] disabled:opacity-50 flex items-center gap-2 font-medium">
                <PaperPlaneRight size={12} weight="bold" />
                {posting ? "Posting…" : session.original_comment_id ? "Update on YouTube" : "Post to YouTube"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Capture pad — silo memory update before Re-draft. */}
      {capturePadOpen && (
        <YTRedraftCapturePad
          siloId={session.silo_id || siloId}
          onClose={(didPush, guidance, persist) => closeCapturePad(didPush, guidance, persist)}
        />
      )}

      {/* Send-to-Silo dialog — reuses the platform's canonical Push-to-silo flow. */}
      {pushDialogOpen && (
        <DocSaveDialog
          open={pushDialogOpen}
          onClose={() => setPushDialogOpen(false)}
          content={draft}
          sourceSiloId={siloId}
          silos={silosForDialog}
          defaultTitle={defaultTitle}
          initialTier={3}
          dialogTitle="Push master reply to silo"
          onSaved={() => { toast.success("Master reply saved into silo."); setPushDialogOpen(false); }}
        />
      )}
    </div>
  );
}

